import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Skeleton, useSkeletonDemo } from '../components/Skeleton'
import { CameraScanner } from '../components/pos/CameraScanner'
import { KioskBoard } from '../components/pos/KioskBoard'
import { tokenInScan } from '../services/kioskTrack'
import { TypingHint } from '../components/pos/TypingHint'
import { LoyaltyDialog } from '../components/pos/LoyaltyDialog'
import { loyaltyAfterSale } from '../services/loyalty'
import type { KitchenTicket, LoyaltyCard } from '../types'
import { finishKitchenTicket, handOverKiosk, subscribeKitchen } from '../services/kitchen'
import { useSearchParams } from 'react-router-dom'
import { EMPTY_SCAN, SCAN_GAP_MS, normalizeBarcode, scanKey, type ScanState } from '../utils/barcode'
import { categoryColor } from '../utils/categoryColors'
import { hallColor } from '../utils/hallTones'
import { allowedHallIds, displayTable, tableLabel, visibleTables } from '../utils/tableLabel'
import { hintSeen, markHintSeen } from '../services/userHints'
import { formatStockNumber, stockState, stockTip } from '../utils/stock'
import { splitPortions } from '../utils/portions'
import { useAuth } from '../context/AuthContext'
import { CART_TABS, lineKey, useCart, type CartTab } from '../context/CartContext'
import { stampWaiting } from '../utils/serving'
import { useCatalog } from '../context/CatalogContext'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { Spinner } from '../components/Spinner'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  LayersMinusIcon,
  ReceiptOffIcon,
  ReceiptTextIcon,
  XIcon,
  NoteIcon,
  PencilIcon,
  PrinterIcon,
  SearchIcon,
  SendToBackIcon,
  NotebookPenIcon,
  GiftIcon,
  InfoIcon,
  CheckIcon,
  ChevronRightIcon,
} from '../components/icons'
import { OtherPaymentSheet } from '../components/pos/OtherPaymentSheet'
import { SplitPaymentSheet, type SplitExtras } from '../components/pos/SplitPaymentSheet'
import { DebtorPicker } from '../components/pos/DebtorPicker'
import { addEntry } from '../services/cari'
import {
  cancelTicket,
  setTicketPrepaid,
  createOrder,
  mergeTickets,
  moveTicket,
  setTicketNote,
  openTicket,
  settleTicket,
  updateTicket,
} from '../services/orders'
import { OpenTickets } from '../components/pos/OpenTickets'
import { TableDialog } from '../components/pos/TableDialog'
import { MoveTicketDialog } from '../components/pos/MoveTicketDialog'
import { NoteDialog } from '../components/pos/NoteDialog'
import { TableNoteDialog } from '../components/pos/TableNoteDialog'
import { PortionPicker } from '../components/pos/PortionPicker'
import { ConfirmDialog, type ConfirmSpec } from '../components/pos/ConfirmDialog'
import { PriceDialog } from '../components/pos/PriceDialog'
import { formatGr, isPendingWeigh, sameLine, totalNetGr } from '../utils/lineMath'
import { WeighDialog } from '../components/pos/WeighDialog'
import { FindTableDialog } from '../components/pos/FindTableDialog'
import { PrintPickDialog } from '../components/pos/PrintPickDialog'
import { ClosedFlash } from '../components/pos/ClosedFlash'
import { Toast } from '../components/pos/Toast'
import { saveTenantSettings, setProductSoldOut } from '../services/products'
import { nextReceiptNo } from '../services/receiptNumber'
import { addPrintedIds, getDeviceCode, getPrintAtTill } from '../utils/deviceId'
import { formatClock, toDayKey } from '../utils/dayKey'
import { applyDiscount, formatTRY } from '../utils/money'
import { describeRule, describeWhen, stackCampaigns } from '../utils/campaigns'
import { mergeLines, newLinesSince, subtractLines } from '../utils/mergeLines'
import { noteOptionsFor } from '../utils/noteOptions'
import { waiterColors } from '../utils/waiterColor'
import type { ReceiptKind } from '../components/receipt/Receipt'
import {
  DEFAULT_STATION,
  PAYMENT_LABELS,
  type NewOrder,
  type Order,
  type OrderLine,
  type DebtorBalance,
  type PaymentMethod,
  type Product,
  type Tare,
  type Table,
} from '../types'
import './SalePage.css'
import { isViewMode, VIEW_MODE_EVENT } from '../services/viewMode'
import { PayActions } from '../components/pos/PayActions'

const EDITING_KEY = 'adissoo.editingTicket'

interface Props {
  /** Hands the finished order up to the shell, which renders and prints it. */
  onPrint: (order: Order, kind?: ReceiptKind) => void
  /** Same, but without printing, keeps it ready for the Ctrl+P reprint. */
  onHold: (order: Order, kind?: ReceiptKind) => void
  lastOrder: Order | null
  /** Queues slips on the shell's printer, one at a time. */
  queuePrint: (tickets: Order[]) => void
  /** Tells the printer a round was rung in here, so it won't print it again. */
  claimTicket: (ticketId: string, lines: Order['lines']) => void
  /** Ids whose slip has come off this till's printer. */
  printedIds: Set<string>
  /** Records a slip printed from this screen. */
  onPrinted: (id: string) => void
}

type Dialog = 'none' | 'other' | 'table' | 'tablePick' | 'split' | 'debtor' | 'tender' | 'bahsis' | 'meal'

/** Dönen çizginin kuyruğu: 100 parça, uzunluk 11 birim (yolun %11'i). */
const ORBIT_TAIL = Array.from({ length: 100 }, (_, j) => {
  const seg = 11 / 100
  // Parçalar biraz üst üste: aralarında kıl payı boşluk kalmasın.
  const len = seg * 1.6
  return {
    dash: `0 ${(j * seg).toFixed(3)} ${len.toFixed(3)} ${(100 - len - j * seg).toFixed(3)}`,
    opacity: Number((((j + 1) / 100) ** 1.8).toFixed(3)),
  }
})

/**
 * Çevrimdışıyken kaydın nereye gittiği (28 Eki 2026): sipariş telefonun
 * kuyruğunda, mutfak internet gelince görüyor. "Masa 5 açıldı" tek başına
 * her şey tamammış gibi duruyordu.
 */
const offlineTail = () => (navigator.onLine ? '' : ', internet gelince mutfağa gider')

export function SalePage({
  onPrint,
  onHold,
  lastOrder,
  queuePrint,
  claimTicket,
  printedIds,
  onPrinted,
}: Props) {
  const { admin, tenantId, discounts, canSettle, canMoveTable, canEditPrice, canOrder, can, hasFeature, trialExpired, payLocked, usePrinter, loyalty } =
    useAuth()
  const { activeCategories, activeTables, halls, productsByCategory, loading: catalogLoading, error, campaigns, products, categories } = useCatalog()
  const skeletonDemo = useSkeletonDemo()
  const loading = catalogLoading || skeletonDemo
  const { openTickets, todayOrders } = useOpenTickets()
  const cart = useCart()
  const [dialog, setDialog] = useState<Dialog>('none')
  /**
   * The open ticket loaded into the cart, held by id rather than by value: a
   * stored copy goes stale the moment the waiter adds a round, and settling
   * from a stale copy would drop that round from both the receipt and the
   * day's takings.
   */
  const [editingId, setEditingId] = useState<string | null>(null)
  const editing = useMemo(
    () => openTickets.find((t) => t.id === editingId) ?? null,
    [editingId, openTickets],
  )
  /**
   * Satışa eklenmiş sadakat kartı (30 Eki 2026): ödül kullanılacaksa indirim
   * olarak uygulanır, ödeme alınınca sunucu damga ekler.
   */
  const [loyaltyPick, setLoyaltyPick] = useState<{ card: LoyaltyCard; useReward: boolean } | null>(null)
  const [loyaltyOpen, setLoyaltyOpen] = useState(false)
  // Sepet boşalınca (temizle, başka masa) kart da satıştan çıkar.
  useEffect(() => {
    if (cart.isEmpty) setLoyaltyPick(null)
  }, [cart.isEmpty])
  /**
   * Kasada kiosk siparişinin üç bölümü (1 Kas 2026, kullanıcı): ödeme
   * bekleyen, hazırlanan, teslim bekleyen. Mutfak ekranı olmayan işletmede
   * kasiyer "Hazır" der: fişler mutfak ekranındaki "Hepsini gönderdim" gibi
   * kapanır, müşterinin telefonu "Hazır" olur. "Teslim edildi" siparişi
   * kasadan kaldırır, telefonda "Teslim edildi" yazar.
   */
  const kioskOn = canSettle && hasFeature('kiosk')
  /** Kiosk sekmesi açık: ürünler yerine üç sütun. */
  const [kioskView, setKioskView] = useState(false)
  const [kitchenTickets, setKitchenTickets] = useState<KitchenTicket[]>([])
  useEffect(() => {
    if (!kioskOn || !tenantId) return
    return subscribeKitchen(tenantId, setKitchenTickets)
  }, [kioskOn, tenantId])
  const { kioskPreparing, kioskToHand } = useMemo(() => {
    const byOrder = new Map<string, KitchenTicket[]>()
    for (const t of kitchenTickets) {
      if (t.status === 'void') continue
      byOrder.set(t.orderId, [...(byOrder.get(t.orderId) ?? []), t])
    }
    const paid = todayOrders
      .filter((o) => o.source === 'kiosk' && o.status === 'completed' && byOrder.has(o.id))
      .sort((a, b) => (a.takeawayNo ?? 0) - (b.takeawayNo ?? 0))
    const tks = (o: Order) => byOrder.get(o.id) ?? []
    return {
      kioskPreparing: paid.filter((o) => tks(o).some((t) => t.status === 'open')),
      kioskToHand: paid.filter((o) => tks(o).every((t) => t.status === 'done' && !t.handedAt)),
    }
  }, [kitchenTickets, todayOrders])
  const [readyBusy, setReadyBusy] = useState<string | null>(null)
  const markKioskHanded = async (o: Order) => {
    setReadyBusy(o.id)
    try {
      await handOverKiosk(o.id)
      const now = Date.now()
      setKitchenTickets((prev) => prev.map((t) => (t.orderId === o.id ? { ...t, status: t.status === 'void' ? t.status : 'done', handedAt: now } : t)))
      say(`Kiosk #${o.takeawayNo ?? '-'} teslim edildi`)
    } catch {
      say('Teslim kaydedilemedi. İnternet bağlantısını kontrol edin.', 'warn')
    } finally {
      setReadyBusy(null)
    }
  }
  const markKioskReady = async (o: Order) => {
    const ids = kitchenTickets.filter((t) => t.orderId === o.id && t.status === 'open').map((t) => t.id)
    setReadyBusy(o.id)
    try {
      for (const id of ids) await finishKitchenTicket(id)
      setKitchenTickets((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, status: 'done' } : t)))
      say(`Kiosk #${o.takeawayNo ?? '-'} hazır, müşterinin telefonuna gitti`)
    } catch {
      say('Hazır işaretlenemedi. İnternet bağlantısını kontrol edin.', 'warn')
    } finally {
      setReadyBusy(null)
    }
  }

  /** Kiosktan gelip kasada ödenmeyi bekleyenler (supabase/kiosk.sql), en eskisi önde. */
  const kioskWaiting = useMemo(
    () =>
      openTickets
        .filter((t) => t.awaitingPayment)
        .sort((a, b) => (a.takeawayNo ?? 0) - (b.takeawayNo ?? 0)),
    [openTickets],
  )

  /**
   * Kampanya: adisyona uyanlar kasada öneri olarak çıkıyor, kasiyer eklerse
   * ödenecekten düşüyor. Birden çok eklenebilir, aynı ürüne ikisi uygulanmaz.
   * Eklenenlerin kimliği tutuluyor, tutarları her çizimde adisyondan yeniden
   * hesaplanıyor: ürün eklenip çıkarılınca indirim de değişsin, kural artık
   * uymuyorsa kendiliğinden düşsün.
   */
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  /** Özeti açık kampanya (ⓘ). */
  const [campaignInfo, setCampaignInfo] = useState<string | null>(null)
  const categoryOf = useMemo(() => {
    const map = new Map(products.map((p) => [p.id, p.categoryId]))
    return (productId: string) => map.get(productId)
  }, [products])
  const { applied: appliedCampaigns, offers: campaignOfferList } = useMemo(
    () =>
      campaigns.length === 0
        ? { applied: [], offers: [] }
        : stackCampaigns(campaigns, campaignIds, cart.lines, categoryOf, new Date()),
    [campaigns, campaignIds, cart.lines, categoryOf],
  )
  /** "Tatlılar: %20 indirim · Hafta içi 14.00-17.00" */
  const campaignSummary = (c: (typeof campaigns)[number]) => {
    const names = [
      ...c.categoryIds.map((id) => categories.find((x) => x.id === id)?.name),
      ...c.productIds.map((id) => products.find((x) => x.id === id)?.name),
    ].filter(Boolean) as string[]
    const when = describeWhen(c)
    return [describeRule(c, names.length ? names.join(', ') : 'Bütün menü'), when].filter(Boolean).join(', ')
  }
  const campaignKurus = appliedCampaigns.reduce((sum, a) => sum + a.kurus, 0)
  /** Bu adisyondan daha önce alınan tutarla ara ödemeler (22 Eki 2026). */
  const prepaidKurus = editing?.prepaidKurus ?? 0
  /**
   * Ara ödemeler tek tek (kullanıcı: "ilkinde kaç, ikincide kaç"): günün
   * satışlarından bu adisyona ait olanlar, alınma sırasıyla. Önceki günden
   * kalan varsa (gece yarısını geçen masa) farkı ayrı satırda.
   */
  const partials = useMemo(
    () =>
      editing
        ? todayOrders
            .filter((o) => o.partialOf === editing.id && o.status === 'completed')
            .sort((a, b) => a.createdAtLocal - b.createdAtLocal)
        : [],
    [editing, todayOrders],
  )
  /**
   * Ödenecek: adisyon eksi kampanya eksi ara ödemeler. Elle indirim bunun
   * üstüne, yani kalana uygulanıyor.
   */
  const payableKurus = Math.max(0, cart.totalKurus - campaignKurus - prepaidKurus)
  // Sadakat ödülü seçildiyse ekranda ve ödeme panelinde indirimli tutar
  // görünsün; kayıtta completeSale aynı indirimi uyguluyor.
  const loyaltyOff = loyaltyPick?.useReward ? applyDiscount(payableKurus, loyalty.rewardPercent) : null
  const dueKurus = loyaltyOff ? loyaltyOff.chargedKurus : payableKurus
  // Başka adisyona ya da sekmeye geçince kampanya seçimi kalmasın.
  useEffect(() => setCampaignIds([]), [editingId, cart.tab])
  /** Cart line whose note is being edited. */
  /** The cart line whose unit price is being overridden, if any. */
  const [pricing, setPricing] = useState<{
    key: string
    name: string
    line: { unitPriceKurus: number; qty: number; productId?: string }
  } | null>(null)
  /** Porsiyonu seçilen ürün (3 Kas 2026); seçince sepete girer. */
  const [portioning, setPortioning] = useState<Product | null>(null)
  /** The product whose per-portion notes are being edited. */
  const [noting, setNoting] = useState<{
    productId: string
    name: string
    byName?: string
    /** Basılan satırın notu ('' = notsuz); pencere o porsiyonda açılır. */
    note?: string
    /** Porsiyonlu üründe hangi porsiyon ("1,5 Porsiyon"); diğerine dokunulmaz. */
    portion?: string
  } | null>(null)
  /** Set when a waiter's round arrived on a ticket the cashier had edited. */
  const [ticketChanged, setTicketChanged] = useState(false)
  /** What the ticket held when it was loaded, to spot a waiter's later round. */
  const loadedLines = useRef<Order['lines'] | null>(null)
  // Watching for waiters' tickets lives on the shell now, so it keeps running
  // while the cashier is on Ayarlar or Rapor. This screen only prints the slips
  // it is asked for by hand.
  /** Whether the move-between-tables dialog is open. */
  const [movingOpen, setMovingOpen] = useState(false)
  /** Masa notu yazılan adisyon (4 Kas 2026). */
  const [noteFor, setNoteFor] = useState<Order | 'pending' | null>(null)
  /** Whether the find-a-table-by-what-they-ate dialog is open. */
  const [finding, setFinding] = useState(false)
  /**
   * An empty table picked on the grid, waiting for the cart to be saved onto
   * it. Held separately from `editing` because nothing exists on the table yet
   *, there is no ticket to load, only a decision about where this one goes.
   */
  const [pendingTable, setPendingTable] = useState<Table | null>(null)
  /**
   * Kasada seçilmiş ama henüz kaydedilmemiş masaların notları, masa kimliğine
   * göre (4 Kas 2026): masa açılırken adisyona yazılıyor. Başka masaya geçip
   * dönünce not yerinde kalsın diye masa başına ayrı tutuluyor.
   */
  const [pendingNotes, setPendingNotes] = useState<Record<string, string>>({})
  const pendingNote = (pendingTable && pendingNotes[pendingTable.id]) || ''
  const setPendingNote = useCallback(
    (note: string) => {
      if (!pendingTable) return
      const id = pendingTable.id
      setPendingNotes((prev) => {
        const next = { ...prev }
        if (note) next[id] = note
        else delete next[id]
        return next
      })
    },
    [pendingTable],
  )
  /**
   * The ticket whose "which lines go on the slip" picker is open.
   *
   * Held as the ticket rather than a flag: the picker is reached both from a
   * correction in the cart and from the printer on a table card, and the card
   * may well not be the ticket that is loaded.
   */
  const [printPick, setPrintPick] = useState<Order | null>(null)
  /**
   * The question on screen, if any. One slot rather than a flag per dialog:
   * only ever one can be up, and each new confirmation would otherwise add
   * another piece of state to keep in step with the rest.
   */
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)
  /**
   * A brief confirmation, cleared after a couple of seconds by the toast.
   *
   * Counted rather than stored as a bare string: saving the same table twice
   * would otherwise leave the message unchanged, the effect would not re-run,
   * and the second save would say nothing.
   */
  const [toast, setToast] = useState<{
    text: string
    tone: 'ok' | 'warn'
    at: number
  } | null>(null)
  const say = useCallback(
    (text: string, tone: 'ok' | 'warn' = 'ok') =>
      setToast({ text, tone, at: Date.now() }),
    [],
  )
  /** Masa ödemeyle kapanınca ortada kısa onay (ClosedFlash). */
  const [closedTable, setClosedTable] = useState<{ name: string; at: number } | null>(null)
  /** Phone only: whether the bottom cart sheet is expanded. */
  const [cartOpen, setCartOpen] = useState(false)
  const peekRef = useRef<HTMLSpanElement>(null)
  /** Top of the menu, for the button that skips the table grid. */
  const menuRef = useRef<HTMLDivElement>(null)

  /**
   * Puts the till back to a blank sale. Four separate paths end here, paid,
   * saved, moved, abandoned, and every one of them has to drop all four
   * pieces of ticket state together, or the next ticket inherits some of the
   * last one's.
   */
  const closeTicket = useCallback(() => {
    cart.clear()
    setCampaignIds([])
    setEditingId(null)
    setTicketChanged(false)
    loadedLines.current = null
    // The chosen table goes with the sale it was chosen for; left behind, the
    // next round would land on it without anyone having said so.
    setPendingTable(null)
  }, [cart])

  /**
   * Moves between the two baskets. The cart itself keeps its own lines, but
   * the loaded ticket belongs to the Masa side alone: carried into Gel-Al it
   * would put a walk-up sale onto someone's table. Leaving the tab drops it,
   * and the basket the cashier returns to is untouched.
   */
  const switchTab = useCallback(
    (next: CartTab) => {
      if (next === cart.tab) return
      if (next === 'takeaway' && editingId !== null) {
        setEditingId(null)
        setTicketChanged(false)
        loadedLines.current = null
        // The ticket's lines go with it. Left in the Masa basket they would
        // look like a fresh round on the way back, and saving that would put a
        // second copy of the whole tab onto the table.
        cart.clearIn('table')
      }
      cart.setTab(next)
    },
    [cart, editingId],
  )

  /**
   * Ana ekrandan gelince (18 Eki 2026): ?sekme=gelal Hızlı Satış, ?sekme=masa
   * Masalar. Sekme açılınca adresten siliniyor; geri dönüşte tekrar
   * uygulanmasın, kasiyerin sonraki seçimi ezilmesin.
   */
  const [search, setSearch] = useSearchParams()
  useEffect(() => {
    const want = search.get('sekme')
    if (!want) return
    switchTab(want === 'masa' ? 'table' : 'takeaway')
    const next = new URLSearchParams(search)
    next.delete('sekme')
    setSearch(next, { replace: true })
  }, [search, setSearch, switchTab])

  /**
   * Empties what is on screen. Two different questions behind one button: a
   * fresh basket is a tidy-up, but a loaded ticket is someone's open tab and
   * clearing it is the first half of closing their table.
   */
  const askClear = useCallback(() => {
    if (cart.isEmpty) return
    setAsk(
      editing
        ? {
            title: `${displayTable(editing.tableName) ?? 'Adisyon'} boşaltılsın mı?`,
            body: 'Masayı kapatırsanız adisyon iptal olur ve listeden düşer. Sadece ürünleri kaldırırsanız adisyon açık kalır, sonra "Değişiklikleri Kaydet" deyin.',
            // Closing is the usual reason for emptying a table, so it is the
            // one on the right; clearing without closing stays available for
            // the cashier who is about to ring the whole thing in again.
            confirmLabel: 'Masayı kapat',
            danger: true,
            // Asks again rather than acting: this scraps a bill with money on
            // it, and the figure is what makes the difference between the
            // right table and the one next to it.
            onConfirm: () =>
              setAsk({
                title: `${displayTable(editing.tableName) ?? 'Adisyon'} kapatılsın mı?`,
                body: (
                  <>
                    {/* What is being scrapped on its own line, and the
                        consequence under it: the figure is what tells the
                        cashier they have the right table, so it should not be
                        read as part of a sentence. */}
                    {/* Only the money is bold: the count is context, the
                        figure is what tells the cashier they have the right
                        table. */}
                    <span className="dlg__figs">
                      {editing.itemCount} ürün, <b>{formatTRY(editing.totalKurus)}</b>
                    </span>
                    <br />
                    Adisyon silinir, ödeme kaydedilmez.
                  </>
                ),
                confirmLabel: 'Kapat',
                danger: true,
                onConfirm: () => {
                  if (!tenantId || !admin) return
                  cancelTicket(tenantId, editing.id, admin.uid)
                  closeTicket()
                },
              }),
            altLabel: 'Ürünleri kaldır',
            onAlt: () => cart.clear(),
          }
        : {
            title: 'Sepet boşaltılsın mı?',
            body: 'Seçilen ürünlerin hepsi sepetten çıkarılır.',
            confirmLabel: 'Sepeti boşalt',
            danger: true,
            onConfirm: () => cart.clear(),
          },
    )
  }, [admin, cart, closeTicket, editing, tenantId])

  // Every category is on screen at once, in menu order. Tabs meant the
  // cashier had to remember which section an item lived in before they could
  // ring it up; scrolling one list is faster than hunting for a tab.
  /**
   * "Bitti" düğmesi hemen yanıt versin (25 Eki 2026): basınca kart o anda
   * değişiyor, kısa bir yazı çıkıyor; sunucudan aynı durum gelince bu geçici
   * değer bırakılıyor. Eskiden kart ancak sunucu cevap verince değişiyordu,
   * arada bir iki saniye hiçbir şey olmuyormuş gibi duruyordu.
   */
  const [soldOverride, setSoldOverride] = useState<Record<string, boolean>>({})
  const [flashId, setFlashId] = useState<string | null>(null)
  useEffect(() => {
    setSoldOverride((o) => {
      const left = Object.entries(o).filter(([id, v]) => products.find((p) => p.id === id)?.soldOut !== v)
      return left.length === Object.keys(o).length ? o : Object.fromEntries(left)
    })
  }, [products])
  const toggleSoldOut = useCallback(
    (p: Product) => {
      if (!tenantId) return
      const next = !p.soldOut
      setSoldOverride((o) => ({ ...o, [p.id]: next }))
      setFlashId(p.id)
      window.setTimeout(() => setFlashId((id) => (id === p.id ? null : id)), 700)
      say(next ? `${p.name} bitti olarak işaretlendi` : `${p.name} yeniden satışta`, next ? 'warn' : 'ok')
      setProductSoldOut(tenantId, p.id, next).catch((err) => {
        console.error('[sale] bitti', err)
        setSoldOverride((o) => {
          const { [p.id]: _gone, ...rest } = o
          return rest
        })
        say('Kaydedilemedi, tekrar deneyin', 'warn')
      })
    },
    [say, tenantId],
  )

  const groups = useMemo(
    () =>
      activeCategories
        .map((category) => ({
          category,
          products: productsByCategory(category.id).map((p) =>
            p.id in soldOverride ? { ...p, soldOut: soldOverride[p.id] } : p,
          ),
        }))
        .filter((g) => g.products.length > 0),
    [activeCategories, productsByCategory, soldOverride],
  )

  /**
   * Garsonda ürün arama (kullanıcı, 21 Eki 2026): harf yazıldıkça liste
   * daralıyor. Türkçe harfler eşdeğer: "cay" Çay'ı, "sis" Şiş'i buluyor,
   * telefon klavyesinde Türkçe harfe uzanmak gerekmiyor.
   */
  const [productQuery, setProductQuery] = useState('')
  const shownGroups = useMemo(() => {
    const q = foldTr(productQuery.trim())
    if (!q) return groups
    return groups
      .map((g) => ({ ...g, products: g.products.filter((p) => foldTr(p.name).includes(q)) }))
      .filter((g) => g.products.length > 0)
  }, [groups, productQuery])

  /** The unpaid ticket sitting on each table, for the grid on the Masa tab. */
  const ticketByTable = useMemo(
    () =>
      new Map(
        openTickets.filter((t) => t.tableName).map((t) => [t.tableName as string, t]),
      ),
    [openTickets],
  )

  /** Masanın tam adı ("Aile → Masa 1"); sipariş masayı bununla tutuyor. */
  const labelOf = useCallback((t: Pick<Table, 'name' | 'hallId'>) => tableLabel(t, halls), [halls])

  /**
   * Garsonun baktığı salonlar (28 Eki 2026, Kullanıcılar > Düzenle > Salon
   * seç). Seçilmemişse null: hepsi. 3 Kas 2026'dan beri kasiyere de
   * (Tuzla Sofrası: aile, hanım, erkek ayrı kasa). Patron ve müdür hepsini görür.
   */
  const allowedHalls = useMemo(
    // Salon atama 30 Eki'den beri paket özelliği değil, herkese açık.
    () =>
      admin?.role === 'waiter' || admin?.role === 'cashier' ? allowedHallIds(admin.perms, halls) : null,
    [admin?.role, admin?.perms, halls],
  )
  const myTables = useMemo(() => visibleTables(activeTables, allowedHalls), [activeTables, allowedHalls])
  /** Garsonun görebileceği açık adisyonlar: kendi salonlarındakiler ve gel-al. */
  const myTickets = useMemo(() => {
    if (!allowedHalls) return openTickets
    const mine = new Set(myTables.map(labelOf))
    return openTickets.filter((t) => !t.tableName || mine.has(t.tableName))
  }, [openTickets, allowedHalls, myTables, labelOf])

  /**
   * Masası artık olmayan (silinmiş, adı değişmiş, kapatılmış) açık adisyonlar.
   * Bütün masalara göre: başka salonun adisyonu garsona yetim görünmesin.
   */
  const orphanTickets = useMemo(() => {
    const names = new Set(activeTables.map(labelOf))
    return openTickets.filter((t) => t.tableName && !names.has(t.tableName))
  }, [openTickets, activeTables, labelOf])

  /**
   * Everyone whose name is on a line of the cart, each with a fixed colour.
   *
   * Derived from the lines rather than kept alongside them: a round removed
   * takes its owner with it, and a ticket that ends up with one contributor
   * again stops showing dots on its own.
   */
  /**
   * A colour per person on the ticket, the opener first.
   *
   * Ordered rather than derived from each name on its own: the header lists
   * the opener ahead of everyone else, and the colours have to be handed out
   * in that same order or the first name would take whatever colour its
   * letters happened to produce.
   */
  const whoColors = useMemo(() => {
    const names: string[] = []
    if (editing?.openedByName) names.push(editing.openedByName)
    for (const l of cart.lines) if (l.byName) names.push(l.byName)
    return waiterColors(names)
  }, [cart.lines, editing])

  /**
   * Whether this person may change a line.
   *
   * A waiter owns what they rang up and nothing else: two of them serving the
   * same table would otherwise edit each other's rounds, a portion made
   * "paket" by someone who never took that order, and no way afterwards to say
   * who did it.
   *
   * The till is exempt. Corrections are its job, the customer is stood in front
   * of it, and a cashier who cannot fix a line has to go find the waiter who
   * rang it up while a queue waits.
   *
   * Lines from before this existed carry no name; those stay editable rather
   * than becoming permanently frozen.
   */
  /** Kasa, ya da patronun "Adisyon düzenleme" verdiği garson (29 Eki 2026). */
  const canFix = canSettle || admin?.perms?.editTicket === true
  const canEditLine = useCallback(
    (line: { byName?: string }) =>
      canFix || !line.byName || line.byName === (admin?.displayName || admin?.username),
    [admin, canFix],
  )

  /**
   * How many of a line were already on the ticket when it was loaded.
   *
   * Anything at or below that count has been through a save, and a save is
   * what sends the slip, so those portions are cooking or cooked. Anything
   * above it was added just now and has been nowhere.
   */
  const savedQty = useCallback(
    (line: OrderLine) => {
      const was = (loadedLines.current ?? []).find((l) => sameLine(l, line))
      return was?.qty ?? 0
    },
    [],
  )

  /**
   * Taking one off a line, refused where the stall is already cooking it.
   *
   * A slip printed is food on a griddle. Removing the line afterwards leaves
   * the kitchen holding a portion that no bill remembers, which is also how
   * someone would help themselves to one. The till may still do it, because a
   * customer who changed their mind is standing in front of it, but it says
   * out loud what it is agreeing to.
   */
  const decrement = useCallback(
    (key: string, line: Order['lines'][number]) => {
      /**
       * What the ticket already held when it was loaded.
       *
       * Anything at or below that count has been saved once, which is what
       * sends a slip, so those portions are the stall's business now. The
       * ticket's own `silent` flag is not consulted: it describes the last
       * save only, and one quiet correction would clear the protection from
       * every slip printed before it.
       */
      const cooked = savedQty(line)
      if (editing && line.qty <= cooked) {
        if (!canFix) {
          say(`Bu ürünün fişi basıldı, kasa azaltabilir.`, 'warn')
          return
        }
        setAsk({
          title: `${line.name} çıkarılsın mı?`,
          body: (
            <>
              <span className="dlg__figs">
                {line.qty} adet, <b>{formatTRY(line.lineTotalKurus)}</b>
              </span>
              <br />
              Bu ürünün fişi tezgaha gitti, hazırlanmış olabilir.
            </>
          ),
          confirmLabel: 'Çıkar',
          danger: true,
          onConfirm: () => cart.decrement(key),
        })
        return
      }
      cart.decrement(key)
    },
    [canFix, cart, editing, say, savedQty],
  )

  /** Flat order across all groups, so the 1-9 shortcuts stay predictable. */
  const flatProducts = useMemo(() => groups.flatMap((g) => g.products), [groups])

  /**
   * Garsonun arama kutusunda yazılıp silinen ürün adları (29 Eki 2026):
   * menüden karışık sekiz tane; menü değişmedikçe aynı sıra.
   */
  const [searchFocused, setSearchFocused] = useState(false)
  // Adların kendisine bağlı: canlı güncelleme (stok, fiyat) sırayı karıştırıp animasyonu baştan başlatmasın.
  const nameKey = flatProducts.map((p) => p.name.trim()).join('\n')
  const hintWords = useMemo(() => {
    const names = [...new Set(nameKey.split('\n').filter((n) => n.length > 0 && n.length <= 28))]
    for (let i = names.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[names[i], names[j]] = [names[j], names[i]]
    }
    return names.slice(0, 8)
  }, [nameKey])

  /** Which counter a product is prepared at, via its category. */
  const stationOf = useCallback(
    (product: Product) =>
      // Ürüne özel tezgah kategorininkinden önce gelir (20 Eki 2026).
      product.station ||
      activeCategories.find((c) => c.id === product.categoryId)?.station ||
      DEFAULT_STATION,
    [activeCategories],
  )

  /**
   * Rings an item up, unless a waiter is reaching for something that has run
   * out.
   *
   * The till may still sell it: the cashier is stood in front of the customer
   * and the stall often finds one more portion, so the flag means "stop
   * offering it", not "refuse it". A waiter is out on the floor with no way to
   * check, and a table promised food that is gone is a worse outcome than a
   * blocked tap.
   */
  /**
   * Tartım ekranı: gel-al'da yeni satır için ya da sepetteki bekleyen
   * (veya yeniden tartılacak) bir satır için.
   */
  const [weighing, setWeighing] = useState<
    | { mode: 'new'; product: Product }
    | { mode: 'line'; key: string; line: OrderLine; product?: Product }
    | null
  >(null)
  const weighOn = hasFeature('tarti')
  /** Menüdeki bütün kaplar: tartımda başka kap seçilebilsin. */
  const knownTares = useMemo(() => {
    const out: Tare[] = []
    for (const p of flatProducts) {
      if (p.tare && !out.some((t) => t.name === p.tare!.name && t.gr === p.tare!.gr)) out.push(p.tare)
    }
    return out
  }, [flatProducts])

  const addProduct = useCallback(
    (product: Product) => {
      // Klavye kısayolu da buradan geçiyor; ürün listesinin gizlenmesi tek
      // başına yetmezdi.
      if (trialExpired) {
        say('Deneme süresi doldu, yeni ürün eklenemiyor', 'warn')
        return
      }
      // Abonelik ödemesi gecikti, salt okunur (2 Kas 2026).
      if (payLocked) {
        say('Abonelik ödemesi gecikti, yeni ürün eklenemiyor', 'warn')
        return
      }
      // Patron bu garsonun sipariş almasını kapattıysa (20 Eki 2026).
      if (!canOrder) {
        say('Sipariş alma bu hesapta kapalı', 'warn')
        return
      }
      if (product.soldOut && !canSettle) {
        say(`${product.name} bitti, kasaya sorun`, 'warn')
        return
      }
      const by = admin?.displayName || admin?.username
      // Tartılı ürün: gel-al'da kasa hemen tartıyor; masada ya da garsonda
      // "tartım bekliyor" satırı, tartım tezgahta.
      if (weighOn && product.byWeight) {
        if (canSettle && cart.tab === 'takeaway' && !editing) {
          setWeighing({ mode: 'new', product })
        } else {
          cart.addPendingWeigh(product, stationOf(product), by)
        }
        return
      }
      // Porsiyonlu ürün (3 Kas 2026): önce porsiyon seçilir.
      if (product.portions?.length) {
        setPortioning(product)
        return
      }
      cart.add(product, stationOf(product), by)
    },
    [admin, canOrder, canSettle, cart, editing, say, stationOf, trialExpired, payLocked, weighOn],
  )

  /**
   * Whether the cart holds anything the loaded ticket did not. Gates the
   * add-to-ticket buttons, so they never appear over a ticket that was only
   * opened to be paid.
   */
  /** Whether anything on the cart is new to the ticket, gates "Masaya Ekle". */
  /**
   * Whether the ticket has genuinely grown, counted per product rather than
   * per line.
   *
   * A note is part of a line's identity, so marking an existing portion
   * "paket" retires one line and creates another, and measured line by line
   * that reads as a new item. It is not: the table is getting the same food,
   * described differently. Counting portions of each product tells the two
   * apart, and only real growth lights "Masaya Ekle".
   */
  const hasAddition = useMemo(() => {
    if (editing === null) return false
    const tally = (lines: Order['lines']) => {
      const m = new Map<string, number>()
      for (const l of lines) {
        const k = `${l.productId} ${l.byName ?? ''}`
        m.set(k, (m.get(k) ?? 0) + l.qty)
      }
      return m
    }
    const before = tally(loadedLines.current ?? [])
    for (const [k, qty] of tally(cart.lines)) {
      if (qty > (before.get(k) ?? 0)) return true
    }
    return false
  }, [cart.lines, editing])

  /**
   * Keys of lines the ticket did not have when it was loaded. Ticks them in
   * the print picker, which is right most of the time, the cashier changes it
   * when a correction means the stall needs something else.
   */
  const addedQty = useMemo(() => {
    /**
     * How many portions of each product the ticket already held, per person.
     * Drawn down as the cart's lines are walked, so a portion that only had a
     * note added spends the credit its old line left behind rather than
     * counting as new.
     *
     * Without this, marking an existing item "paket" retires one line and
     * creates another, and the new one goes to the stall, a second portion
     * cooked for an order that never grew.
     */
    const pool = new Map<string, number>()
    for (const l of loadedLines.current ?? []) {
      const k = `${l.productId} ${l.byName ?? ''}`
      pool.set(k, (pool.get(k) ?? 0) + l.qty)
    }

    const qty = new Map<string, number>()
    for (const line of cart.lines) {
      const k = `${line.productId} ${line.byName ?? ''}`
      const credit = pool.get(k) ?? 0
      const covered = Math.min(credit, line.qty)
      pool.set(k, credit - covered)
      const grown = line.qty - covered
      if (grown > 0) qty.set(lineKey(line), grown)
    }
    return qty
  }, [cart.lines])

  /**
   * Whether the cart differs from the ticket at all, a line removed or a
   * quantity reduced as much as something added.
   *
   * This used to watch only for growth, because a slip cannot un-cook food and
   * so only additions were printed. But a wrongly rung item has to come off the
   * bill, and there was no way to save that: the correction sat on screen and
   * the customer was charged for it anyway.
   */
  const hasEdit = useMemo(() => {
    if (editing === null) return false
    const before = loadedLines.current ?? []
    if (before.length !== cart.lines.length) return true
    return cart.lines.some((line) => {
      // Whose round it is counts (byName), and which weighing (weigh.id).
      const was = before.find((l) => sameLine(l, line))
      // Tartılan satırın adedi aynı kalabilir; tutarı değişir.
      return !was || was.qty !== line.qty || was.lineTotalKurus !== line.lineTotalKurus
    })
  }, [cart.lines, editing])

  const completeSale = useCallback(
    (
      method: PaymentMethod,
      extras: Partial<NewOrder> & { discountPercent?: number } = {},
      /**
       * Whether to print, when the caller has an opinion.
       *
       * Left undefined it decides for itself, and the rule is whether a slip
       * has been to the stalls yet. A waiter's ticket was printed when it was
       * rung in, so settling it only needs closing. A sale rung straight up at
       * the till has no slip at all, the customer is standing there waiting to
       * be told where to collect, so that one must print.
       */
      print?: boolean,
    ) => {
      if (!tenantId || !admin || cart.isEmpty) return null
      // Görüntüleme modu: sepet yerinde kalsın, fiş basılmasın.
      if (isViewMode()) {
        window.dispatchEvent(new Event(VIEW_MODE_EVENT))
        return null
      }

      // Settling from a cart that no longer matches the ticket would record a
      // total for one set of items against a different set, the waiter's last
      // round served but not charged. The cashier has to reconcile first.
      if (ticketChanged) {
        setAsk({
          title: 'Adisyon değişti',
          body: 'Garson bu adisyona ekleme yaptı. Ödeme almadan önce "Yenile" ile güncel adisyonu alın.',
          confirmLabel: 'Tamam',
        })
        return null
      }

      const { discountPercent: picked, ...rest } = extras
      // Sadakat ödülü: elle indirim seçilmediyse kartın indirimi uygulanır.
      const rewardOn = !!loyaltyPick?.useReward && !(picked && picked > 0)
      const discountPercent = rewardOn ? loyalty.rewardPercent : picked

      // Fiş kuralı: Gel-Al basılır, garsonun adisyonu basılmaz (o fiş girilirken
      // çıktı). Bu cihazda "Fişleri kasa bilgisayarından bas" açıksa (21 Eki
      // 2026) basılmıyor, sipariş işaretleniyor; otomatik yazdırması açık kasa
      // basıyor.
      // Yazıcısız kurumda (28 Eki 2026) hiçbir satış basmıyor.
      // Kiosk siparişi hiç basılmadı: ödenince Gel-Al gibi basılır (30 Eki 2026).
      const kioskSale = editing?.awaitingPayment === true
      const wantPrint = (print ?? (!editing || kioskSale)) && usePrinter
      const remote = wantPrint && getPrintAtTill()
      // Günün Gel-Al sırası: masasız satışların en büyüğünden bir fazlası.
      const takeawayNo = editing
        ? undefined
        : Math.max(0, ...todayOrders.filter((o) => !o.tableName).map((o) => o.takeawayNo ?? 0)) + 1

      // totalKurus is always what was actually charged; the pre-discount
      // figure is kept alongside so the day report can show both. A campaign
      // comes off first, a hand-picked discount applies to what is left.
      const discounted =
        discountPercent && discountPercent > 0
          ? applyDiscount(payableKurus, discountPercent)
          : null
      const orderCampaigns = appliedCampaigns.map((a) => ({
        id: a.campaign.id,
        name: a.campaign.name,
        kurus: a.kurus,
      }))
      // Ara ödeme alınmış adisyon: toplam (ürünler) ara toplamda, alınan
      // kalan totalKurus'ta; ürün satışları raporda bir kez sayılıyor.
      const reduced = Boolean(discounted || orderCampaigns.length || prepaidKurus > 0)

      const draft: NewOrder = {
        receiptNo: nextReceiptNo(),
        dayKey: toDayKey(),
        deviceId: getDeviceCode(),
        cashierUid: admin.uid,
        cashierEmail: admin.email,
        lines: cart.lines,
        itemCount: cart.itemCount,
        totalKurus: discounted ? discounted.chargedKurus : payableKurus,
        ...(reduced && { subtotalKurus: cart.totalKurus }),
        ...(discounted && {
          discountKurus: discounted.discountKurus,
          discountPercent,
        }),
        ...(orderCampaigns.length > 0 && { campaigns: orderCampaigns }),
        ...(prepaidKurus > 0 && { prepaidKurus }),
        paymentMethod: method,
        status: 'completed',
        ...(takeawayNo && { takeawayNo }),
        ...(remote && { printAtTill: true }),
        ...rest,
      }

      // Settling a waiter's ticket updates it in place, so the ticket keeps
      // its table and the name of whoever opened it. A walk-up sale at the
      // till has no ticket and writes a fresh order.
      const id = editing
        ? (settleTicket(tenantId, editing.id, {
            receiptNo: draft.receiptNo,
            paymentMethod: method,
            // The cart's lines, not the ticket's: a price corrected at the
            // till for something sold by weight has to reach the record, or
            // the receipt and the day's takings disagree with what was
            // charged.
            lines: cart.lines,
            itemCount: cart.itemCount,
            totalKurus: draft.totalKurus,
            ...(reduced && { subtotalKurus: cart.totalKurus }),
            ...(discounted && {
              discountKurus: discounted.discountKurus,
              discountPercent,
            }),
            ...(orderCampaigns.length > 0 && { campaigns: orderCampaigns }),
            ...(prepaidKurus > 0 && { prepaidKurus }),
            ...(remote && { printAtTill: true }),
            // Kiosk: ödeme alındı, mutfak tetiği fişleri şimdi düşürür.
            ...(kioskSale && { awaitingPayment: false }),
            ...rest,
          }),
          editing.id)
        : // Not awaited: offline this promise never settles, and the local
          // cache has already applied the write by the time it returns.
          createOrder(tenantId, draft)

      const settled: Order = {
        ...draft,
        ...(editing && {
          tableName: editing.tableName,
          // Kiosk siparişinin sıra numarası fişte "GEL-AL #12" diye çıksın.
          ...(editing.takeawayNo && { takeawayNo: editing.takeawayNo }),
          openedByName: editing.openedByName,
          createdAtLocal: editing.createdAtLocal,
        }),
        id,
        createdAt: null,
        createdAtLocal: editing?.createdAtLocal ?? Date.now(),
        pending: true,
      }
      // A walk-up sale prints; settling a ticket that already has paper out
      // does not. Held either way, so the Ctrl+P reprint can still produce this
      // slip when a customer asks for it after paying.
      if (wantPrint && !remote) onPrint(settled, 'cash')
      else onHold(settled)
      // Recorded either way. Closing without printing still means this ticket
      // is done, and the auto-print watcher must not pick it up as new work.
      addPrintedIds([id])
      onPrinted(id)
      // Sadakat: damga sunucuda kurala göre eklenir (ödül kullanıldıysa önce düşer).
      if (loyaltyPick) {
        const { card } = loyaltyPick
        loyaltyAfterSale(tenantId, card.code, id, draft.totalKurus, rewardOn)
          .then((c) =>
            say(
              rewardOn
                ? `Ödül kullanıldı, ${card.code}: ${c.stamps}/${loyalty.target} damga`
                : `Damga eklendi, ${card.code}: ${c.stamps}/${loyalty.target}`,
            ),
          )
          .catch(() => say(`Damga eklenemedi, ${card.code}`, 'warn'))
        setLoyaltyPick(null)
      }
      if (editing?.tableName) setClosedTable({ name: displayTable(editing.tableName), at: Date.now() })
      closeTicket()
      setDialog('none')
      // Returned so a caller that has to record something alongside the sale
      //, putting it on an account, can point at the order it came from.
      // Null means nothing was recorded.
      return id
    },
    [admin, appliedCampaigns, cart, closeTicket, editing, loyalty, loyaltyPick, onHold, onPrint, onPrinted, payableKurus, prepaidKurus, say, tenantId, ticketChanged, todayOrders],
  )

  /**
   * Puts the sale on someone's account.
   *
   * The order is completed with `credit` rather than left open: the food has
   * gone out and the till is done with it. What is outstanding is recorded
   * against the person, not against the table, so the table is free for the
   * next customer, which is the whole reason a restaurant writes a debt rather
   * than leaving a ticket open all evening.
   *
   * The entry is written after the sale so a failure here cannot swallow the
   * sale itself; the sale is the thing that must not be lost. A debt that
   * failed to record can be added by hand from the Cari screen, and it says so.
   */
  const settleToDebtor = useCallback(
    async (debtor: DebtorBalance, note: string) => {
      if (!tenantId || !admin) return
      const amountKurus = payableKurus
      // Fiş kuralı diğer ödemelerle aynı (21 Eki 2026): Gel-Al basılıyor,
      // garsonun adisyonu basılmıyor (o fiş girilirken çıktı). Eskiden hep
      // false'tu; Gel-Al'ı cariye yazınca sipariş fişi hiç çıkmıyordu.
      const orderId = completeSale(
        'credit',
        { note: note ? `Cari: ${debtor.name}, ${note}` : `Cari: ${debtor.name}` },
      )

      try {
        await addEntry(tenantId, {
          debtorId: debtor.id,
          kind: 'debt',
          amountKurus,
          orderId: orderId ?? undefined,
          // Kişinin kendi sayfasında görünüyor; orada adı yazmak bir şey
          // söylemiyor, açıklama yazıldıysa o söylüyor.
          description: note || debtor.name,
          createdByName: admin.displayName || admin.username,
        })
      } catch (err) {
        console.error('[cari] borç yazılamadı', err)
        setAsk({
          title: 'Borç kaydedilemedi',
          body:
            `Satış kaydedildi ama ${debtor.name} hesabına yazılamadı. ` +
            'Cari ekranından elle eklemeniz gerekiyor.',
          confirmLabel: 'Tamam',
        })
      }
    },
    [admin, payableKurus, completeSale, tenantId],
  )

  /**
   * Saves the institution's discount rates, from inside the discount sheet.
   *
   * Owner only: a cashier applies a rate, an owner decides which rates exist
   *, the same split the menu already draws around prices. Passed as
   * undefined for everyone else, which is what hides the editing controls.
   */
  const saveDiscountRates = useCallback(
    async (next: number[]) => {
      if (!tenantId) return
      await saveTenantSettings(tenantId, { discounts: next })
    },
    [tenantId],
  )

  /**
   * Tutarla ara ödeme (kullanıcı, 22 Eki 2026): "1.500'ün 800'ünü ben
   * veriyorum". Ürünsüz bir satış olarak kaydediliyor (kasa ve ödeme türü
   * raporu doğru çıksın), adisyon açık kalıyor ve ödenen toplamı artıyor;
   * kapanışta yalnız kalan alınıyor. Tutar kalanın tamamıysa adisyon olağan
   * yoldan kapanıyor (ürünler o satışta, bir kez).
   */
  const settleAmount = useCallback(
    (amountKurus: number, method: PaymentMethod, extras: SplitExtras, print: boolean) => {
      if (!tenantId || !admin || !editing || amountKurus <= 0) return
      if (isViewMode()) {
        window.dispatchEvent(new Event(VIEW_MODE_EVENT))
        return
      }
      if (ticketChanged) {
        setAsk({
          title: 'Adisyon değişti',
          body: 'Garson bu adisyona ekleme yaptı. Ödeme almadan önce "Yenile" ile güncel adisyonu alın.',
          confirmLabel: 'Tamam',
        })
        return
      }

      const { bahsisKurus, bahsisPaymentMethod, mealCard, debtor } = extras
      const due = Math.max(0, editing.totalKurus - prepaidKurus)

      // Kalanın tamamı: adisyon kapanıyor, kasadaki Öde'nin aynısı.
      if (amountKurus >= due) {
        if (debtor) void settleToDebtor(debtor.debtor, debtor.note)
        else
          completeSale(
            method,
            {
              ...(bahsisKurus && bahsisKurus > 0 && { bahsisKurus, bahsisPaymentMethod }),
              ...(method === 'meal' && mealCard && { mealCard }),
            },
            print,
          )
        return
      }

      const draft: NewOrder = {
        receiptNo: nextReceiptNo(),
        dayKey: toDayKey(),
        deviceId: getDeviceCode(),
        cashierUid: admin.uid,
        cashierEmail: admin.email,
        tableName: editing.tableName,
        openedByName: editing.openedByName,
        openedByUid: editing.openedByUid,
        lines: [],
        itemCount: 0,
        totalKurus: amountKurus,
        partialOf: editing.id,
        paymentMethod: method,
        ...(bahsisKurus && bahsisKurus > 0 && { bahsisKurus, bahsisPaymentMethod }),
        ...(method === 'meal' && mealCard && { mealCard }),
        note: debtor
          ? `Ara ödeme, Cari: ${debtor.debtor.name}${debtor.note ? `, ${debtor.note}` : ''}`
          : 'Ara ödeme',
        ...(print && getPrintAtTill() && { printAtTill: true }),
        status: 'completed',
      }
      const id = createOrder(tenantId, draft)
      setTicketPrepaid(tenantId, editing.id, prepaidKurus + amountKurus)

      if (debtor) {
        void addEntry(tenantId, {
          debtorId: debtor.debtor.id,
          kind: 'debt',
          amountKurus,
          orderId: id,
          description: debtor.note || debtor.debtor.name,
          createdByName: admin.displayName || admin.username,
        }).catch((err) => {
          console.error('[cari] ara ödeme borcu yazılamadı', err)
          setAsk({
            title: 'Borç kaydedilemedi',
            body:
              `Ara ödeme kaydedildi ama ${debtor.debtor.name} hesabına yazılamadı. ` +
              'Cari ekranından elle eklemeniz gerekiyor.',
            confirmLabel: 'Tamam',
          })
        })
      }

      const settled: Order = { ...draft, id, createdAt: null, createdAtLocal: Date.now(), pending: true }
      if (print && !getPrintAtTill()) onPrint(settled, 'cash')
      else onHold(settled)
      addPrintedIds([id])
      onPrinted(id)
      say(`${formatTRY(amountKurus)} alındı, kalan ${formatTRY(due - amountKurus)}`)
    },
    [admin, completeSale, editing, onHold, onPrint, onPrinted, prepaidKurus, say, settleToDebtor, tenantId, ticketChanged],
  )

  /**
   * Settles one person's share of an open ticket as its own completed order,
   * separate from the ticket itself.
   *
   * The ticket is not settled through settleTicket here: that always marks
   * the whole thing paid and stamps one receipt number for the whole total,
   * which is exactly what a split payment is not. Each share becomes its own
   * createOrder call, its own id, its own receipt number, its own printable
   * slip, while the original ticket shrinks to whatever is left.
   *
   * When nothing is left, the original ticket is voided rather than settled:
   * settleTicket always wants a receipt number and a charge, and this ticket
   * took neither itself, every kuruş already went out through the parts.
   * Forcing a zero-value "completed" row through it would need a fake receipt
   * number, and nextReceiptNo()'s daily recovery trusts every completed row's
   * number as one that was actually printed.
   */
  const settlePart = useCallback(
    (
      linesTaken: OrderLine[],
      method: PaymentMethod,
      extras: SplitExtras,
      print: boolean,
    ) => {
      if (!tenantId || !admin || !editing || linesTaken.length === 0) return
      if (isViewMode()) {
        window.dispatchEvent(new Event(VIEW_MODE_EVENT))
        return
      }

      // Same guard as completeSale: a share must not be settled against a
      // ticket a waiter has since changed underneath the cashier.
      if (ticketChanged) {
        setAsk({
          title: 'Adisyon değişti',
          body: 'Garson bu adisyona ekleme yaptı. Ödeme almadan önce "Yenile" ile güncel adisyonu alın.',
          confirmLabel: 'Tamam',
        })
        return
      }

      const { discountPercent, bahsisKurus, bahsisPaymentMethod, mealCard, debtor } = extras
      const rawTotal = linesTaken.reduce((sum, l) => sum + l.lineTotalKurus, 0)
      const discounted =
        discountPercent && discountPercent > 0 ? applyDiscount(rawTotal, discountPercent) : null

      const draft: NewOrder = {
        receiptNo: nextReceiptNo(),
        dayKey: toDayKey(),
        deviceId: getDeviceCode(),
        cashierUid: admin.uid,
        cashierEmail: admin.email,
        tableName: editing.tableName,
        openedByName: editing.openedByName,
        openedByUid: editing.openedByUid,
        lines: linesTaken,
        itemCount: linesTaken.reduce((n, l) => n + l.qty, 0),
        totalKurus: discounted ? discounted.chargedKurus : rawTotal,
        ...(discounted && {
          subtotalKurus: rawTotal,
          discountKurus: discounted.discountKurus,
          discountPercent,
        }),
        paymentMethod: method,
        // Parçalı ödemede Üstü Kalsın (20 Eki 2026): o parçayı ödeyenin
        // bıraktığı fark, ana kasadaki gibi ayrı kayıt, ciroya katılmıyor.
        ...(bahsisKurus && bahsisKurus > 0 && { bahsisKurus, bahsisPaymentMethod }),
        // Yemek kartı ve cari (21 Eki 2026): kasadaki gibi.
        ...(method === 'meal' && mealCard && { mealCard }),
        ...(debtor && {
          note: debtor.note ? `Cari: ${debtor.debtor.name}, ${debtor.note}` : `Cari: ${debtor.debtor.name}`,
        }),
        // Yazıcısız cihaz: fişi kasa bilgisayarı basacak (completeSale'deki gibi).
        ...(print && getPrintAtTill() && { printAtTill: true }),
        status: 'completed',
      }

      const id = createOrder(tenantId, draft)

      // Cari'ye yazılan pay: borç kaydı satıştan sonra, satış kaybolmasın
      // (settleToDebtor'daki sıra).
      if (debtor) {
        void addEntry(tenantId, {
          debtorId: debtor.debtor.id,
          kind: 'debt',
          amountKurus: draft.totalKurus,
          orderId: id,
          description: debtor.note || debtor.debtor.name,
          createdByName: admin.displayName || admin.username,
        }).catch((err) => {
          console.error('[cari] parça borcu yazılamadı', err)
          setAsk({
            title: 'Borç kaydedilemedi',
            body:
              `Pay kaydedildi ama ${debtor.debtor.name} hesabına yazılamadı. ` +
              'Cari ekranından elle eklemeniz gerekiyor.',
            confirmLabel: 'Tamam',
          })
        })
      }

      const settled: Order = {
        ...draft,
        id,
        createdAt: null,
        createdAtLocal: Date.now(),
        pending: true,
      }
      if (print && !getPrintAtTill()) onPrint(settled, 'cash')
      else onHold(settled)
      addPrintedIds([id])
      onPrinted(id)

      const remaining = subtractLines(editing.lines, linesTaken)
      if (remaining.lines.length === 0) {
        cancelTicket(tenantId, editing.id, admin.uid)
        if (editing.tableName) setClosedTable({ name: displayTable(editing.tableName), at: Date.now() })
        setDialog('none')
        closeTicket()
      } else {
        updateTicket(tenantId, editing.id, remaining.lines, remaining.itemCount, remaining.totalKurus)
        // Dialog stays open on purpose: `editing` is live, so SplitPaymentSheet
        // re-renders with the shrunk ticket automatically, several people
        // paying in a row costs one tap each instead of reopening this sheet.
      }
    },
    [admin, closeTicket, editing, onHold, onPrint, onPrinted, tenantId, ticketChanged],
  )

  /**
   * Merges the cart into a ticket that is already open on the table, rather
   * than opening a second one beside it. Quantities of the same item with the
   * same note combine, so a second round of two teas reads as four.
   */
  const addCartToTicket = useCallback(
    (ticket: Order) => {
      if (!tenantId || cart.isEmpty) return

      const merged = mergeLines(ticket.lines, cart.lines)
      const { itemCount, totalKurus } = merged
      // Sipariş Takip: the new round starts waiting now.
      const lines = stampWaiting(ticket.lines, merged.lines)

      updateTicket(tenantId, ticket.id, lines, itemCount, totalKurus, false)
      // A round rung in on this device prints through the flow that added it,
      // not through the watcher that exists to catch other devices' rounds.
      claimTicket(ticket.id, lines)
      say(`${displayTable(ticket.tableName) ?? 'Adisyon'} adisyonuna eklendi${offlineTail()}`)
      closeTicket()
      setDialog('none')
    },
    [cart, claimTicket, closeTicket, say, tenantId],
  )

  /**
   * Till: saves an addition back onto the open ticket, optionally printing a
   * slip for the new round alone.
   *
   * The cashier can add to a ticket the same way a waiter does, a customer
   * asks for two more teas at the counter. What comes off the printer is only
   * what was added: the rest of the ticket has been to the stalls already, and
   * reprinting it would send the kitchen food it has cooked.
   */
  const saveAddition = useCallback(
    /**
     * @param print   Whether a slip follows, when the caller has no list.
     * @param pickedLines  Exactly which lines to print, chosen by hand in the
     *   picker. An empty array means "save, print nothing", different from
     *   undefined, which falls back to printing whatever was added.
     */
    (print: boolean, pickedLines?: Order['lines']) => {
      if (!tenantId || !editing) return

      // Every line taken off. Saving an empty ticket would leave a table
      // sitting open at zero, which nobody would ever settle, closing it is
      // what emptying a bill actually means. Asked in our own dialog rather
      // than the browser's, which arrives in the wrong language and voice.
      if (cart.isEmpty) {
        setAsk({
          title: `${displayTable(editing.tableName) ?? 'Adisyon'} kapatılacak`,
          body: 'Adisyonda ürün kalmadı. Onaylarsanız masa kapatılır ve açık adisyonlardan düşer.',
          confirmLabel: 'Masayı kapat',
          danger: true,
          onConfirm: () => {
            if (!admin) return
            cancelTicket(tenantId, editing.id, admin.uid)
            closeTicket()
          },
        })
        return
      }

      const before = loadedLines.current ?? []
      const added = newLinesSince(before, cart.lines)

      updateTicket(
        tenantId,
        editing.id,
        stampWaiting(editing.lines, cart.lines),
        cart.itemCount,
        cart.totalKurus,
        !print,
      )
      // Claimed before the write comes back, so the auto-print watcher sees
      // this round as already handled. Without it the till's own addition
      // would arrive as a waiter's round and print a second time.
      claimTicket(editing.id, cart.lines)

      // `print` with no explicit selection means the automatic answer: what
      // was added since the ticket was loaded.
      const toPrint = pickedLines ?? (print ? added.lines : [])

      if (toPrint.length > 0) {
        // Printed as the ticket it belongs to, same table, same waiter, so
        // the stall can match it against what is already on the pass.
        queuePrint([
          {
            ...editing,
            lines: toPrint,
            itemCount: toPrint.reduce((n, l) => n + l.qty, 0),
            totalKurus: toPrint.reduce((n, l) => n + l.lineTotalKurus, 0),
            // A fresh id: the ticket's own id is already recorded as printed,
            // and reusing it would make the queue treat this as a duplicate.
            id: `${editing.id}+${Date.now()}`,
            createdAtLocal: Date.now(),
          },
        ])
      }

      closeTicket()
      setDialog('none')
    },
    [admin, cart, claimTicket, closeTicket, editing, queuePrint, tenantId],
  )

  /** Waiter: saves the cart as an open ticket against a table. */
  const saveTicket = useCallback(
    /**
     * @param silent  True for a correction that should not reach the stalls.
     *   Both of the waiter's buttons come through here, and they mean
     *   different things: "Masaya Ekle" sends the new round to be cooked,
     *   "Değişiklikleri Kaydet" only records a change. Without this they were
     *   the same press and every correction printed.
     */
    (table: { name: string }, silent = false) => {
      if (!tenantId || !admin || cart.isEmpty) return

      // Editing an existing ticket keeps its table, so the picker is skipped
      // entirely and this saves straight back to the same ticket.
      if (editing) {
        updateTicket(
          tenantId,
          editing.id,
          stampWaiting(editing.lines, cart.lines),
          cart.itemCount,
          cart.totalKurus,
          silent,
        )
        // Only claimed when nothing is meant to print. Claiming tells this
        // device's printer the round is already handled; doing that on "Masaya
        // Ekle" would swallow the very slip it was pressed to produce, on a
        // till where the waiter and the printer are the same machine.
        if (silent) claimTicket(editing.id, cart.lines)
        // Says which of the two presses this was. The screen goes back to a
        // blank cart either way, so without naming the outcome a waiter cannot
        // tell whether the stall was told, and a waiter who is not sure taps
        // again and doubles the round.
        say(
          silent
            ? navigator.onLine
              ? `${displayTable(editing.tableName) ?? 'Adisyon'} kaydedildi, fiş çıkmadı`
              : `${displayTable(editing.tableName) ?? 'Adisyon'} kaydedildi, internet gelince gönderilir`
            : !navigator.onLine
              ? `${displayTable(editing.tableName) ?? 'Adisyon'} kaydedildi${offlineTail()}`
              : usePrinter
                ? `${displayTable(editing.tableName) ?? 'Adisyon'} kaydedildi, fiş yazdırıldı`
                : `${displayTable(editing.tableName) ?? 'Adisyon'} kaydedildi, mutfağa gitti`,
        )
      } else {
        // Last line of defence against a second ticket on the same table: the
        // dialog already routes a busy table to the ticket on it, but two
        // waiters can be mid-save at the same moment.
        const busy = openTickets.find((t) => t.tableName === labelOf(table))
        if (busy) {
          addCartToTicket(busy)
          if (pendingNote) setTicketNote(tenantId, busy.id, pendingNote)
          if (pendingNote) setPendingNote('')
          return
        }
        openTicket(tenantId, {
          dayKey: toDayKey(),
          deviceId: getDeviceCode(),
          cashierUid: admin.uid,
          cashierEmail: admin.email,
          tableName: labelOf(table),
          openedByName: admin.displayName || admin.username,
          openedByUid: admin.uid,
          // Sipariş Takip: a new table's whole round waits from now.
          lines: stampWaiting([], cart.lines),
          itemCount: cart.itemCount,
          totalKurus: cart.totalKurus,
          tableNote: pendingNote || undefined,
          status: 'open',
        },
        // Arrives late or not at all, offline it never fires. Said out loud
        // when it does: the cart has already been cleared by then, so a
        // silent rejection left the waiter believing a table was open when
        // nothing had been written.
        () => say(`${displayTable(labelOf(table))} açılamadı, tekrar deneyin`, 'warn'))
        say(`${displayTable(labelOf(table))} açıldı${offlineTail()}`)
        if (pendingNote) setPendingNote('')
      }

      closeTicket()
      setDialog('none')
    },
    [addCartToTicket, admin, cart, claimTicket, closeTicket, editing, openTickets, pendingNote, say, setPendingNote, tenantId],
  )


  /** Moves an open ticket to another table: a customer changed seats. */
  const onMoveTicket = useCallback(
    (ticket: Order, tableName: string) => {
      if (!tenantId) return
      moveTicket(tenantId, ticket.id, tableName)
      // The cart holds this ticket's lines if it was loaded for editing; its
      // table just changed underneath, so drop the stale copy.
      if (editingId === ticket.id) closeTicket()
      setMovingOpen(false)
    },
    [closeTicket, editingId, tenantId],
  )

  /** Two tables pushed together: the target absorbs the source, which closes. */
  const onMergeTickets = useCallback(
    (source: Order, target: Order) => {
      if (!tenantId || !admin) return
      const { lines, itemCount, totalKurus } = mergeLines(target.lines, source.lines)
      mergeTickets(tenantId, source, target, lines, itemCount, totalKurus, admin.uid)
      if (editingId === source.id || editingId === target.id) closeTicket()
      setMovingOpen(false)
    },
    [admin, closeTicket, editingId, tenantId],
  )

  /**
   * "Ürüne dokununca not" ipucu. Garsonların aklına sepetteki ürüne basmak
   * gelmiyordu (kullanıcı, 2 Eki 2026). Her hesaba bir kez (22 Eki 2026):
   * ilk gösterildiği anda hesaba "gördü" yazılıyor, o açılışta × ile ya da
   * not penceresi açılınca kapanıyor, sonraki girişlerde hiçbir cihazda
   * çıkmıyor. Hesap okunamazsa eskisi gibi cihazdaki işarete bakılıyor.
   */
  const localHintOff = () => {
    try {
      return localStorage.getItem('adissoo.noteHint') === 'off'
    } catch {
      return false
    }
  }
  const [noteHint, setNoteHint] = useState(false)
  const hintMarked = useRef(false)
  useEffect(() => {
    let live = true
    void hintSeen('note').then((seen) => {
      if (!live) return
      if (seen === null) return setNoteHint(!localHintOff())
      if (seen) return
      // Bu cihazda zaten kapatılmışsa hesaba da yaz, bir daha sorulmasın.
      if (localHintOff()) {
        hintMarked.current = true
        void markHintSeen('note')
        return
      }
      setNoteHint(true)
    })
    return () => {
      live = false
    }
  }, [])
  // Ekranda ilk göründüğü an: hesaba yaz, sonraki girişlerde çıkmasın.
  const hintVisible = noteHint && !cart.isEmpty
  useEffect(() => {
    if (!hintVisible || hintMarked.current) return
    hintMarked.current = true
    void markHintSeen('note')
  }, [hintVisible])
  const hideNoteHint = useCallback(() => {
    setNoteHint(false)
    try {
      localStorage.setItem('adissoo.noteHint', 'off')
    } catch {
      // Depolama yoksa hesaptaki işaret yeter.
    }
  }, [])

  const onEditNote = useCallback(
    (line: { productId: string; name: string; byName?: string; note?: string; portion?: string }) => {
      hideNoteHint()
      // Carries whose portions are being edited: the same product can sit on
      // the ticket twice, once per person, and the dialog must show and rewrite
      // only the half it was opened from.
      setNoting({ productId: line.productId, name: line.name, byName: line.byName, note: line.note, portion: line.portion })
    },
    [hideNoteHint],
  )

  /**
   * Cart lines grouped by product, so an item ordered several ways reads as one
   * heading with its variants beneath rather than the same name repeated.
   * Groups keep the order the products were rung up in.
   */
  const cartGroups = useMemo(() => {
    const groups = new Map<
      string,
      { productId: string; name: string; byName?: string; portion?: string; lines: typeof cart.lines }
    >()
    for (const line of cart.lines) {
      // One heading per product, however many people put something under it.
      // Two waiters serving the same table used to get a heading each, so the
      // item read as two unrelated things; here it stays one item with its
      // portions listed, the same shape a noted product already has.
      // Tartılı satır kendi satırında: tabakları ve durumu ayrı okunsun.
      // Porsiyonlar da ayrı başlık (3 Kas 2026): "Kuru Fasulye (1,5 Porsiyon)".
      const gkey = line.weigh
        ? `${line.productId} w ${line.weigh.id}`
        : line.portion
          ? `${line.productId} p ${line.portion}`
          : line.productId
      const group = groups.get(gkey)
      if (group) group.lines.push(line)
      else
        groups.set(gkey, {
          productId: line.productId,
          name: line.name,
          byName: line.byName,
          portion: line.portion,
          lines: [line],
        })
    }
    return [...groups.values()]
  }, [cart.lines])

  /** Tick-box options for the product being noted; empty means free text only. */
  const notingOptions = useMemo(() => {
    if (!noting) return []
    const product = flatProducts.find((p) => p.id === noting.productId)
    return product ? noteOptionsFor(product, activeCategories) : []
  }, [activeCategories, flatProducts, noting])

  /**
   * Every portion of the product being noted, flattened out of however many
   * lines it currently occupies: a previous edit may already have split it, and
   * the dialog numbers the portions 1..n regardless.
   */
  const notingPortions = useMemo(() => {
    if (!noting) return []
    return cart.lines
      .filter(
        (l) =>
          l.productId === noting.productId &&
          (l.byName ?? '') === (noting.byName ?? '') &&
          (l.portion ?? '') === (noting.portion ?? ''),
      )
      .flatMap((l) => Array.from({ length: l.qty }, () => l.note ?? ''))
  }, [cart.lines, noting])

  /**
   * Bu ürünün adisyona kaydedilmiş, mutfağa gitmiş porsiyonları (20 Eki 2026):
   * not penceresinde kilitli. Eskiden yenmiş porsiyonun notu da
   * değiştirilebiliyordu; yeni porsiyon yalnız kalanlar.
   */
  const notingSent = useMemo(() => {
    if (!noting || !editing) return []
    return editing.lines
      .filter(
        (l) =>
          l.productId === noting.productId &&
          (l.byName ?? '') === (noting.byName ?? '') &&
          (l.portion ?? '') === (noting.portion ?? '') &&
          !l.weigh,
      )
      .flatMap((l) => Array.from({ length: l.qty }, () => l.note ?? ''))
  }, [editing, noting])
  const notingSplit = useMemo(() => splitPortions(notingPortions, notingSent), [notingPortions, notingSent])

  /**
   * Pencerenin açılacağı porsiyon (22 Eki 2026). Hep 1. porsiyonda açılıyordu:
   * ikinci kez eklenen "Normal" satıra basan garson kutuda birincinin notunu
   * görüyor, ikinci ürün o notu almış sanıyordu; yazdığı da birinciye gidiyordu.
   * Basılan satırın porsiyonu; ürün adına basıldıysa 1. porsiyon.
   */
  const notingStart = useMemo(() => {
    if (noting?.note === undefined) return 0
    return Math.max(0, notingSplit.fresh.indexOf(noting.note))
  }, [noting, notingSplit])

  const onCancelTicket = useCallback(
    (ticket: Order) => {
      if (!tenantId || !admin) return
      setAsk({
        title: `${displayTable(ticket.tableName) ?? 'Adisyon'} iptal edilsin mi?`,
        body: (
          <>
            <span className="dlg__figs">
              {ticket.itemCount} ürün, <b>{formatTRY(ticket.totalKurus)}</b>
            </span>
            <br />
            Adisyon silinir, ödeme kaydedilmez.
            {/* Ara ödemeler ayrı satış olarak kayıtlı (22 Eki 2026). */}
            {ticket.prepaidKurus ? (
              <>
                <br />
                Alınan {formatTRY(ticket.prepaidKurus)} ara ödeme kayıtlı kalır; iade gerekiyorsa elle yapın.
              </>
            ) : null}
          </>
        ),
        confirmLabel: 'Adisyonu iptal et',
        danger: true,
        onConfirm: () => {
          cancelTicket(tenantId, ticket.id, admin.uid)
          if (editing?.id === ticket.id) {
            closeTicket()
          }
        },
      })
    },
    [admin, closeTicket, editing, tenantId],
  )

  /** Loads an open ticket into the cart, to add to it or to settle it. */
  const pickTicket = useCallback(
    (ticket: Order) => {
      if (editing?.id === ticket.id) {
        closeTicket()
        return
      }
      // Tabs are the till's. A waiter has one basket and no way back from the
      // other one, so switching theirs moved the ticket somewhere they could
      // not see: the round they had just rung up vanished and saved nothing.
      const target: CartTab = canSettle ? 'table' : cart.tab
      // At the till a ticket is a table's, so it loads into the Masa basket,
      // dropped into Gel-Al it would sit on top of a walk-up sale in progress.
      if (canSettle) cart.setTab(target)
      cart.replaceIn(target, ticket.lines)
      loadedLines.current = ticket.lines
      setTicketChanged(false)
      setEditingId(ticket.id)
    },
    [canSettle, cart, closeTicket, editing],
  )

  /**
   * Açık adisyon yenilemeden sağ çıkıyor.
   *
   * Yalnızca kimliği saklanıyor, satırları değil: adisyonun kendisi zaten
   * sunucuda ve yenileme sonrası oradan en güncel haliyle geliyor. Adisyonlar
   * henüz yüklenmediği için geri yükleme onlar gelene kadar bekliyor; bu
   * arada kasiyer başka bir masa seçerse onunki geçerli.
   */
  const restoreId = useRef<string | null>(null)
  if (restoreId.current === null) {
    try {
      restoreId.current = sessionStorage.getItem(EDITING_KEY) ?? ''
    } catch {
      restoreId.current = ''
    }
  }

  useEffect(() => {
    if (editingId !== null) restoreId.current = ''
    try {
      if (editingId) sessionStorage.setItem(EDITING_KEY, editingId)
      else if (!restoreId.current) sessionStorage.removeItem(EDITING_KEY)
    } catch {
      // Depolama yoksa yenileyince masa kapalı açılır, o kadar.
    }
  }, [editingId])

  useEffect(() => {
    const id = restoreId.current
    if (!id || editingId !== null) return
    const ticket = openTickets.find((t) => t.id === id)
    if (!ticket) return
    restoreId.current = ''
    pickTicket(ticket)
  }, [editingId, openTickets, pickTicket])

  /**
   * A table tapped on the grid. A busy one loads its ticket, which is the
   * cashier's way in to settling it. An empty one is only a destination,
   * there is nothing to load yet, so it is remembered and the cart is saved
   * there when the round is rung up.
   */
  const onPickTable = useCallback(
    (table: Table, ticket: Order | null) => {
      if (ticket) {
        // Loading a ticket overwrites the basket. Unsaved lines that nobody
        // asked to discard would vanish without a trace, so this asks first,
        // the Masa basket now survives tab switches, so there is more chance
        // of something sitting in it than there used to be.
        const unsaved = editingId === null && !cart.isEmpty
        if (unsaved) {
          setAsk({
            title: 'Sepetteki ürünler kaydedilmedi',
            body: `${displayTable(labelOf(table))} adisyonu açılırsa sepettekiler silinecek.`,
            confirmLabel: 'Adisyonu aç',
            danger: true,
            onConfirm: () => {
              pickTicket(ticket)
              setPendingTable(null)
            },
          })
          return
        }
        pickTicket(ticket)
        setPendingTable(null)
        return
      }
      // Tapping the table already chosen clears it, so a mis-tap is undone the
      // same way it was made.
      setPendingTable((prev) => (prev?.id === table.id ? null : table))
      if (editingId !== null) closeTicket()
    },
    [cart.isEmpty, closeTicket, editingId, pickTicket],
  )

  /**
   * Pulls a waiter's new round into the open cart.
   *
   * The cashier can have the ticket on screen when the waiter adds to it, and
   * settling from the copy loaded a minute ago would charge for the old
   * contents, the round would be served but never paid for. So the cart is
   * refreshed from the ticket, but only while the cashier has not touched it
   * themselves; their own edits are never thrown away silently.
   */
  useEffect(() => {
    if (!editing || !loadedLines.current) return

    // Compared by content, not reference: every snapshot hands back a fresh
    // array even when nothing changed, so a reference check would fire on each
    // one and refuse to fire on a genuine edit that happened to be re-sent.
    const loaded = JSON.stringify(loadedLines.current)
    const current = JSON.stringify(editing.lines)
    if (loaded === current) return

    const untouched = JSON.stringify(cart.lines) === loaded

    loadedLines.current = editing.lines
    if (untouched) cart.replace(editing.lines)
    else setTicketChanged(true)
  }, [cart, editing])

  /**
   * The ticket left the open list while it was loaded, another till settled
   * it, or it was voided or merged elsewhere. Holding a cart that no longer
   * belongs to anything invites charging for it twice.
   */
  useEffect(() => {
    if (editingId && !editing) {
      closeTicket()
    }
  }, [closeTicket, editing, editingId])

  // Keeps the item just added in view on the collapsed bar. Without this the
  // line only ever shows the oldest items, which is the opposite of useful,
  // the tap being confirmed is the newest one.
  useEffect(() => {
    if (cartOpen) return
    const el = peekRef.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [cart.lines, cartOpen])

  // Paying or clearing empties the cart; an expanded sheet would be left
  // holding half the screen for nothing.
  useEffect(() => {
    if (cart.isEmpty) setCartOpen(false)
  }, [cart.isEmpty])

  /** Discards the cashier's edits and takes the ticket as it now stands. */
  const reloadTicket = useCallback(() => {
    if (!editing) return
    cart.replace(editing.lines)
    loadedLines.current = editing.lines
    setTicketChanged(false)
  }, [cart, editing])

  /**
   * Whether the sale waiting behind the tender question also prints. The two
   * paying buttons differ only in this, and the question sits between the
   * press and the sale, so the answer has to be held somewhere.
   */
  const [tenderPrint, setTenderPrint] = useState(false)

  /**
   * What money left on top of the bill is called here. The same record either
   * way, only the word differs, because it is the same transaction to the
   * till and a different thing to the people at it.
   */

  /** Tartılmamış satır var: ödeme kilitli (tutarı henüz belli değil). */
  const weighPending = cart.lines.some(isPendingWeigh)

  const askTender = useCallback((print: boolean) => {
    if (cart.isEmpty || weighPending) return
    setTenderPrint(print)
    setDialog('tender')
  }, [cart.isEmpty, weighPending])

  const onOther = useCallback(() => {
    if (!cart.isEmpty && !weighPending) setDialog('other')
  }, [cart.isEmpty, weighPending])

  const reprint = useCallback(() => {
    if (lastOrder) onPrint(lastOrder, 'cash')
  }, [lastOrder, onPrint])

  /**
   * Barkod okuyucu (16 Eki 2026): okutulan barkod → ürün. Bütün ürünlerden
   * aranıyor ki gizli bir ürün "bulunamadı" yerine "gizli" desin.
   */
  const byBarcode = useMemo(() => {
    const map = new Map<string, Product>()
    for (const p of products) for (const b of p.barcodes ?? []) map.set(normalizeBarcode(b), p)
    return map
  }, [products])
  const scanRef = useRef<ScanState>(EMPTY_SCAN)
  /** Bekleyen 1-9 kısayolu: okutmanın ilk rakamı olabilir diye bir an bekliyor. */
  const shortcutTimer = useRef<number | null>(null)

  /**
   * Okutulan barkodu sepete ekler; sonucu yazı olarak döner. Kasada USB
   * okuyucu bunu kısa uyarı olarak gösteriyor, garsonun kamera penceresi
   * kendi altında.
   */
  const scanToCart = useCallback(
    (code: string): { text: string; ok: boolean } => {
      const p = byBarcode.get(normalizeBarcode(code))
      if (!p) return { text: `Barkod bulunamadı: ${code}`, ok: false }
      if (!p.active || !flatProducts.includes(p)) {
        return { text: `${p.name} gizli, Menü'den açılabilir`, ok: false }
      }
      // addProduct'ın kendi engelleri: kamera penceresi de bilsin.
      if (trialExpired) return { text: 'Deneme süresi doldu, yeni ürün eklenemiyor', ok: false }
      if (payLocked) return { text: 'Abonelik ödemesi gecikti, yeni ürün eklenemiyor', ok: false }
      if (p.soldOut && !canSettle) return { text: `${p.name} bitti, kasaya sorun`, ok: false }
      addProduct(p)
      return { text: `${p.name} eklendi`, ok: true }
    },
    [addProduct, byBarcode, canSettle, flatProducts, trialExpired, payLocked],
  )

  /**
   * Kiosk takibi (31 Eki 2026): müşterinin telefonundaki karekod ya da
   * söylediği 4 haneli kod, ödeme bekleyen kiosk siparişini açar.
   */
  const kioskByScan = useCallback(
    (code: string) => {
      const token = tokenInScan(code, kioskWaiting.map((o) => o.trackToken ?? ''))
      if (token) return kioskWaiting.find((o) => o.trackToken === token)
      const digits = code.replace(/\D/g, '')
      return digits.length === 4 ? kioskWaiting.find((o) => o.pickupCode === digits) : undefined
    },
    [kioskWaiting],
  )

  const onBarcode = useCallback(
    (code: string) => {
      const kiosk = canSettle ? kioskByScan(code) : undefined
      if (kiosk) {
        pickTicket(kiosk)
        say(`Kiosk #${kiosk.takeawayNo ?? '-'} açıldı`)
        return
      }
      const r = scanToCart(code)
      if (!r.ok) say(r.text, 'warn')
    },
    [canSettle, kioskByScan, pickTicket, say, scanToCart],
  )

  /**
   * Garsonun kamerayla okutması (20 Eki 2026): üst çubuktaki barkod düğmesi
   * (POSLayout) 'adissoo:scan' olayını yayıyor, pencere burada açılıyor.
   */
  const [scanning, setScanning] = useState(false)
  useEffect(() => {
    const open = () => setScanning(true)
    window.addEventListener('adissoo:scan', open)
    return () => window.removeEventListener('adissoo:scan', open)
  }, [])

  // Keyboard is where a till earns its speed.
  useEffect(() => {
    const cancelShortcut = () => {
      if (shortcutTimer.current !== null) {
        window.clearTimeout(shortcutTimer.current)
        shortcutTimer.current = null
      }
    }
    const onKey = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)

      // Tartım ekranı açıkken klavye ona ait: rakamlar gram, Esc onu kapatır.
      // Yoksa 1'e basınca ürün ekleniyordu.
      if (weighing) return
      // Porsiyon penceresi de kendi Esc'ini dinliyor.
      if (portioning) return

      if (e.key === 'Escape') {
        setDialog('none')
        setNoting(null)
        return
      }
      if (typing || dialog !== 'none' || noting) return

      // Barkod okuyucu: hızlı gelen karakterler bir okutma. Tamamlanınca ürün
      // ekleniyor; okutmanın parçası olan tuşlar kısayol olarak işlenmiyor.
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const scan = scanKey(scanRef.current, e.key, performance.now())
        scanRef.current = scan.state
        if (scan.code) {
          e.preventDefault()
          cancelShortcut()
          onBarcode(scan.code)
          return
        }
        if (scan.burst) {
          cancelShortcut()
          return
        }
      }

      // F1 settles and prints, F2 settles without: the same two endings the
      // buttons carry, and like the buttons they now ask cash-or-card first
      // rather than recording a bare "paid".
      if (e.key === 'F1' && canSettle) {
        e.preventDefault()
        askTender(true)
      } else if (e.key === 'F2' && canSettle) {
        e.preventDefault()
        askTender(false)
      } else if (e.key === 'F3' && canSettle) {
        e.preventDefault()
        onOther()
      } else if (e.key === 'F4' && canSettle) {
        // One key for both directions: at the counter the question is only ever
        // "the other one", and two keys would be two things to remember.
        e.preventDefault()
        switchTab(cart.tab === 'takeaway' ? 'table' : 'takeaway')
      } else if (e.key === 'Delete' && canSettle) {
        // Same boundary as the button: emptying a tab is the counter's call.
        e.preventDefault()
        askClear()
      } else if (e.key.toLowerCase() === 'p' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        reprint()
      } else if (/^[1-9]$/.test(e.key)) {
        const product = flatProducts[Number(e.key) - 1]
        // Bir an bekliyor: hemen ardından hızlı bir karakter gelirse bu bir
        // barkod okutmasının ilk rakamıdır, kısayol değil.
        if (product) {
          cancelShortcut()
          shortcutTimer.current = window.setTimeout(() => {
            shortcutTimer.current = null
            addProduct(product)
          }, SCAN_GAP_MS + 15)
        }
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      cancelShortcut()
    }
  }, [askTender, cart, canSettle, completeSale, dialog, flatProducts, noting, onBarcode, onOther, portioning, reprint, stationOf, switchTab, weighing])

  if (loading) return <Skeleton kind="grid" label="Menü yükleniyor" />
  if (error) return <div className="sale sale--center"><p className="sale__error">{error}</p></div>

  /** Masa ızgarasındaki bir kutu; masası olmayan adisyonlar da aynı kutuyu kullanıyor. */
  // Hook değil: yukarıdaki erken dönüşlerin altında.
  const tableGroups = (() => {
    const known = new Set(halls.map((h) => h.id))
    const out = halls
      .map((h) => ({ key: h.id, title: h.name, tables: myTables.filter((t) => t.hallId === h.id) }))
      .filter((g) => g.tables.length > 0)
    const loose = myTables.filter((t) => !t.hallId || !known.has(t.hallId))
    if (loose.length > 0) out.push({ key: 'salonsuz', title: out.length > 0 ? 'Salonsuz' : '', tables: loose })
    return out
  })()
  // Salonun rengi (22 Eki 2026), Masa Yönetimi ve Masa Seç ile aynı.
  const hallStyle = (key: string): CSSProperties | undefined =>
    halls.length > 0 ? ({ '--hall': hallColor(key === 'salonsuz' ? null : key, halls) } as CSSProperties) : undefined

  const tableTile = (t: Table, ticket: Order | null) => {
    const active = editing?.tableName === labelOf(t) || pendingTable?.id === t.id
    return (
                  <div
                    key={t.id}
                    className={`tbl ${ticket ? 'tbl--busy' : ''} ${active ? 'tbl--on' : ''}`}
                  >
                    <button
                      type="button"
                      className="tbl__main"
                      onClick={() => onPickTable(t, ticket)}
                    >
                      <span className="tbl__name">
                        {t.name}
                        {ticket?.tableNote && (
                          <span className="tbl__note" title={ticket.tableNote} aria-label="Masa notu var">
                            <NoteIcon />
                          </span>
                        )}
                        {/* Faint, and beside the table rather than under it:
                            it settles an argument about whose table this is,
                            but it is not what the cashier came to read. */}
                        {ticket?.openedByName && (
                          <span className="tbl__by">
                            <span className="tbl__by-dot">, </span>
                            <span className="tbl__by-name">{ticket.openedByName}</span>
                          </span>
                        )}
                      </span>
                      {/* The running total is why a cashier taps a busy table,
                          so it is on the card rather than one tap further in. */}
                      <span className="tbl__sub">
                        {!ticket
                          ? 'Boş'
                          : ticket.prepaidKurus
                            ? (
                              // Ara ödeme alınmış masa (22 Eki 2026): kalan.
                              <>
                                Kalan {formatTRY(Math.max(0, ticket.totalKurus - ticket.prepaidKurus))}
                                <small className="tbl__paid">Ara ödeme {formatTRY(ticket.prepaidKurus)}</small>
                              </>
                            )
                            : formatTRY(ticket.totalKurus)}
                      </span>
                    </button>
                    {ticket && (
                      <div className="tbl__side">
                        {/* Cancel on top, print below. The destructive one is
                            furthest from the card body, so a thumb sliding off
                            the table name lands on the printer rather than on
                            the button that scraps the bill. */}
                        {canSettle && (
                          <button
                            type="button"
                            className="tbl__x"
                            onClick={() => onCancelTicket(ticket)}
                            title="Adisyonu iptal et"
                            aria-label={`${t.name} adisyonunu iptal et`}
                          >
                            ×
                          </button>
                        )}
                        {usePrinter && (
                        <button
                          type="button"
                          // Asks what to print rather than reprinting the lot:
                          // most of a table's bill has already been to the
                          // stalls, and a full reprint sends food that is
                          // already cooked.
                          onClick={() => setPrintPick(ticket)}
                          title="Fiş yazdır"
                          aria-label={`${t.name} fiş yazdır`}
                        >
                          <PrinterIcon />
                        </button>
                        )}
                      </div>
                    )}
                  </div>
    )
  }

  return (
    <div className={`sale ${cart.isEmpty ? 'sale--cart-empty' : ''}`}>
      <section className="catalog">
        {/* Only the till has two ways to end a sale, so only the till gets the
            choice. A waiter can do one thing, put it on a table, and a pair
            of tabs on their screen would be asking a question with one answer. */}
        {canSettle && (
          <div className="saletabs" role="tablist" aria-label="Satış türü">
            {CART_TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={cart.tab === t && !kioskView}
                className={`saletabs__tab saletabs__tab--${t} ${cart.tab === t && !kioskView ? 'saletabs__tab--on' : ''}`}
                onClick={() => {
                  setKioskView(false)
                  switchTab(t)
                }}
              >
                {t === 'takeaway' ? 'Gel-Al' : 'Masa'}
              </button>
            ))}
            {/* Kiosk sekmesi (1 Kas 2026, kullanıcı): dar, sayısıyla. */}
            {kioskOn && (
              <button
                type="button"
                role="tab"
                aria-selected={kioskView}
                className={`saletabs__tab saletabs__tab--kiosk ${kioskView ? 'saletabs__tab--on' : ''}`}
                onClick={() => setKioskView(true)}
              >
                Kiosk
                {kioskWaiting.length > 0 && <span className="saletabs__badge">{kioskWaiting.length}</span>}
              </button>
            )}
          </div>
        )}

        {kioskOn && kioskView ? (
          <KioskBoard
            waiting={kioskWaiting}
            preparing={kioskPreparing}
            toHand={kioskToHand}
            activeId={editingId}
            busyId={readyBusy}
            onPick={pickTicket}
            onReady={(o) => void markKioskReady(o)}
            onHand={(o) => void markKioskHanded(o)}
          />
        ) : (
        <>

        {/* The waiter's view of the tables. The till has the grid below
            instead, which says the same things per table and says them about
            the empty ones too, two lists of the same tickets, one under the
            other, was the same information twice. */}
        {!canSettle && (
        <OpenTickets
          tickets={myTickets}
          activeId={editing?.id ?? null}
          onPick={pickTicket}
          onCancel={onCancelTicket}
          // Asks what to print, same as the till's table cards: most of a
          // ticket has already been to the stalls by the time anyone reaches
          // for the printer.
          onPrint={usePrinter ? (ticket) => setPrintPick(ticket) : undefined}
          canCancel={canSettle}
          printedIds={printedIds}
          onOpenMove={() => setMovingOpen(true)}
          onPickTable={canOrder ? () => setDialog('tablePick') : undefined}
          pickedTable={displayTable((pendingTable && labelOf(pendingTable)) ?? editing?.tableName) ?? null}
          // Only the waiter's screen folds: the till is a laptop with room for
          // the whole list, and picking a ticket out of it is what it is for.
          foldable={!canSettle}
        />
        )}

        {/* Every defined table, not just the busy ones: the cashier looks at
            this to find a table by where it is in the room, and a grid that
            reshuffles as tables fill would move the one they were reaching for.
            Above the menu because on this tab the table is chosen first. */}
        {canSettle && cart.tab === 'table' && activeTables.length > 0 && (
          <>
            <div className="tables__head">
              <h2>
                Masalar
                {openTickets.length > 0 && `, ${openTickets.length} açık adisyon`}
              </h2>
              {/* The grid pushes the menu off the bottom once there are more
                  than a few tables, and the cashier's next move after picking
                  one is always to ring something up. */}
              <button
                type="button"
                className="tickets__move"
                onClick={() =>
                  menuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                title="Ürün listesine in"
              >
                <ChevronDownIcon />
                Ürünler
              </button>
              {/* Someone at the counter who does not know their table number.
                  Reading twenty tickets aloud to find them is the thing this
                  replaces. */}
              {/* Her zaman görünür: açık masa yokken kaybolan düğme, patronun
                  "bu yetki bende yok mu" diye sormasına yol açıyordu. Boşken
                  pencere "açık adisyon yok" diyor. */}
              <button
                type="button"
                className="tickets__move"
                onClick={() => setFinding(true)}
                title="Yediklerinden masayı bul"
              >
                <SearchIcon />
                Masa bul
              </button>
              {/* Moving a ticket is a correction that belongs to no single
                  table, so it sits above the grid rather than on a card. */}
              {canMoveTable && (
                <button
                  type="button"
                  className="tickets__move"
                  onClick={() => setMovingOpen(true)}
                  title="Adisyonu başka masaya taşı"
                >
                  <SendToBackIcon />
                  Taşı
                </button>
              )}
            </div>
            {/* Salon salon (22 Eki 2026), Masa Seç penceresiyle aynı: masalar
                katalogda salon ve ada göre sıralı geliyor, burada yalnız
                başlıklarla bölünüyor. Salon yoksa başlıksız tek ızgara. */}
            {/* Her salon kendi kutusunda (26 Eki 2026), garsonun Masa Seç
                ekranındaki gibi: kutu salonun renginde geçişli. */}
            {tableGroups.map((g) => (
              <section key={g.key} className="tables__group" style={hallStyle(g.key)}>
                {g.title && <h3 className="tables__hall">{g.title}</h3>}
                <div className="tables">
                  {g.tables.map((t) => tableTile(t, ticketByTable.get(labelOf(t)) ?? null))}
                </div>
              </section>
            ))}

            {/* Masası olmayan adisyonlar (21 Eki 2026): masası silinmiş ya da
                adı adisyon açıkken değişmiş. Eskiden kasada hiç görünmüyor,
                ödeme sistemden alınamıyordu. */}
            {orphanTickets.length > 0 && (
              <>
                <div className="tables__head tables__head--orphan">
                  <h2>Masası olmayan adisyonlar ({orphanTickets.length})</h2>
                </div>
                <p className="tables__note">
                  Masası silinmiş ya da adı değişmiş. Açıp ödemesini alabilir ya da Taşı ile bir masaya geçirebilirsiniz.
                </p>
                <div className="tables">
                  {orphanTickets.map((ticket) =>
                    tableTile(
                      { id: `yetim-${ticket.id}`, name: ticket.tableName as string, sortOrder: 0, active: true },
                      ticket,
                    ),
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Where the "Ürünler" button lands. Its own element rather than the
            first category, whose sticky heading would be scrolled under. */}
        <div ref={menuRef} className="menu-anchor" />

        {/* Deneme bitti: yeni sipariş yok, ama masalar ve sepet yerinde,
            süre dolmadan açılmış hesaplar kapatılabilsin. */}
        {trialExpired && (
          <div className="trial-lock">
            <strong>Deneme süreniz doldu</strong>
            <span>
              Yeni sipariş alınamıyor. Açık masaların hesabını kapatabilir,
              raporlarınıza bakabilirsiniz. Devam etmek için{' '}
              <b style={{ userSelect: 'all' }}>info@adissoo.com</b> adresine yazın.
            </span>
          </div>
        )}

        {/* Garsonun sipariş alması kapalı (20 Eki 2026): menü görünüyor ama
            ürün eklenmiyor; masalar ve takip açık. */}
        {!canOrder && !trialExpired && (
          <div className="trial-lock">
            <strong>Sipariş alma kapalı</strong>
            <span>Bu hesapla ürün eklenemiyor. Açmak için patron: Ayarlar › Kullanıcılar › Sipariş alabilir.</span>
          </div>
        )}

        {/* Kiosk (1 Kas 2026): siparişler Kiosk sekmesinde; burada yalnız
            ödeme bekleyen varsa tek satır uyarı, dokununca sekme açılır. */}
        {kioskOn && kioskWaiting.length > 0 && (
          <button type="button" className="kwaitnote" onClick={() => setKioskView(true)}>
            <span className="kwaitnote__n">{kioskWaiting.length}</span>
            <span>kiosk siparişi ödeme bekliyor</span>
            <ChevronRightIcon />
          </button>
        )}

        {/* Yalnız garsonda: kasada klavye kısayolları ve barkod okuyucu var,
            odak bu kutudayken okutulan barkod kutuya yazılırdı. */}
        {!canSettle && !trialExpired && groups.length > 0 && (
          <div className="psearch">
            <SearchIcon />
            <input
              type="search"
              className="psearch__input"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setProductQuery('')
              }}
              placeholder={searchFocused || hintWords.length === 0 ? 'Ürün ara' : ''}
              aria-label="Ürün ara"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="search"
            />
            {!productQuery && !searchFocused && hintWords.length > 0 && <TypingHint words={hintWords} />}
            {productQuery && (
              <button
                type="button"
                className="psearch__clear"
                onClick={() => setProductQuery('')}
                aria-label="Aramayı temizle"
              >
                <XIcon />
              </button>
            )}
          </div>
        )}

        {!trialExpired && groups.length === 0 && <p className="items__empty">Menüde aktif ürün yok.</p>}
        {!trialExpired && groups.length > 0 && shownGroups.length === 0 && (
          <p className="items__empty">"{productQuery.trim()}" ile eşleşen ürün yok.</p>
        )}

        {!trialExpired && shownGroups.map(({ category, products }) => (
          <div
            key={category.id}
            className="grp"
            // Kategorinin rengi (22 Eki 2026): başlık ve ürün kartları onun tonunda.
            style={{ '--cat': categoryColor(category, categories).hex } as CSSProperties}
          >
            <h2>{category.name}</h2>
            <div className="items">
              {products.map((p) => {
                const shortcut = flatProducts.indexOf(p) + 1
                return (
                  <div
                    key={p.id}
                    className={`item ${p.soldOut ? 'item--out' : ''} ${stockState(p) === 'ok' || stockState(p) === 'low' ? 'item--left' : ''} ${flashId === p.id ? 'item--flash' : ''}`}
                  >
                    {/* Sold out still sells at the till: the stall often finds
                        one more portion, and the cashier is stood in front of
                        the customer and can ask. A waiter is out on the floor
                        with no way to check, so for them it is simply off,
                        otherwise a table is promised food that is gone. */}
                    <button
                      type="button"
                      className="item__main"
                      onClick={() => addProduct(p)}
                    >
                      {shortcut <= 9 && <span className="item__key">{shortcut}</span>}
                      <span className="item__name">{p.name}</span>
                      <span className="item__price">
                        {p.soldOut ? 'Bitti' : formatTRY(p.priceKurus)}
                        {/* Stok rozeti varken kısa ("/kg"): rozetle aynı satıra sığsın,
                            fiyat iki satıra bölünüp kart üçe çıkmasın (20 Eki 2026). */}
                        {!p.soldOut && weighOn && p.byWeight && (
                          <small className="item__kg">
                            {stockState(p) === 'ok' || stockState(p) === 'low' ? '/kg' : ' / kg ⚖'}
                          </small>
                        )}
                        {/* Rozetle düğmeye yer (29 Eki 2026): sığıyorsa fiyatla aynı
                            satırda, sığmıyorsa bu boşluk alta iner, kart ancak
                            o zaman uzar. Rozet ve düğme köşede, bu boşluğun üstünde. */}
                        {(stockState(p) === 'ok' || stockState(p) === 'low') && (
                          <i
                            className="item__room"
                            aria-hidden="true"
                            style={{ width: `${Math.ceil(formatStockNumber(p).length * 5 + 12) + (canSettle ? 26 : 2)}px` }}
                          />
                        )}
                      </span>
                    </button>
                    {/* Stoklu üründe kalan (20 Eki 2026): stok düğmesinin solunda
                        yalnız sayı, üzerine gelince (telefonda dokununca)
                        "3 adet kaldı". Eskiden ad ile fiyat arasında ayrı bir
                        satırdı, kart üç satıra çıkıyordu. Bittiyse fiyat
                        yerinde zaten "Bitti" yazıyor. */}
                    {(stockState(p) === 'ok' || stockState(p) === 'low') && (
                      <span
                        className={`item__left is-${stockState(p)} ${canSettle ? '' : 'is-alone'}`}
                        tabIndex={0}
                        data-tip={stockTip(p)}
                        aria-label={stockTip(p)}
                      >
                        {formatStockNumber(p)}
                      </span>
                    )}
                    {/* Only the till marks stock: a waiter reports it to the
                        counter rather than changing the menu from the floor.
                        Straight to the flag rather than through a menu, this
                        is decided at a glance, mid-service. */}
                    {canSettle && (
                      <button
                        type="button"
                        className={`item__stock ${p.soldOut ? 'is-out' : ''}`}
                        onClick={() => toggleSoldOut(p)}
                        title={
                          p.soldOut
                            ? `${p.name}: bitti işaretli, satışa aç`
                            : `${p.name}: stok bitti olarak işaretle`
                        }
                        aria-pressed={p.soldOut ?? false}
                      >
                        <LayersMinusIcon />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        </>
        )}
      </section>

      <aside
        className={`cart ${cartOpen ? 'cart--open' : ''} ${
          cart.isEmpty ? 'cart--empty' : ''
        } ${editing ? 'cart--editing' : ''}`}
      >
        {/* Phones only (hidden by CSS elsewhere): the cart is a sheet at the
            bottom, and this bar is what it collapses to. The running total has
            to stay readable even when the lines are folded away. */}
        {/* Nothing to look at on an empty cart: opening it would show "Ürün
            seçin", a zero total and a disabled button across half the screen. */}
        <div className="cart__peekrow">
        <button
          type="button"
          className="cart__peek"
          onClick={() => setCartOpen((v) => !v)}
          disabled={cart.isEmpty}
        >
          <span className="cart__peek-main">
            {/* What was just rung up, in order, rather than a count: the tap
                that matters is confirming the right item went in, and "7 ürün"
                doesn't answer that. Newest last, so the tail is the latest. */}
            <span className="cart__peek-items" ref={peekRef}>
              {/* On a loaded ticket the bar says which table, and nothing else:
                  a running list of what was just tapped sat above a second bar
                  saying the same table's name, and two stacked strips reading
                  almost the same thing is what made the screen confusing. */}
              {editing ? (
                <>
                  Düzenleniyor: <b>{displayTable(editing.tableName) ?? 'Adisyon'}</b>
                </>
              ) : cartOpen || cart.isEmpty ? (
                cart.isEmpty ? (
                  'Ürün seçin'
                ) : pendingTable ? (
                  // Açıkken seçili masanın adı (25 Eki 2026, kullanıcı):
                  // "Adisyonu kapat" ödemeyi kapatmak sanılıyordu; paneli
                  // kapatmayı zaten sağdaki ok söylüyor.
                  <b>{displayTable(labelOf(pendingTable))}</b>
                ) : canSettle && cart.tab === 'takeaway' ? (
                  'Gel-Al'
                ) : (
                  'Masa seçilmedi'
                )
              ) : (
                cart.lines
                  .map((l) => (l.qty > 1 ? `${l.qty}× ${l.name}` : l.name))
                  .join(', ')
              )}
            </span>
            {/* The total is on the ticket's own line below; repeating it here
                put the same figure twice on two strips that already looked
                alike. */}
            {!editing && (
              <span className="cart__peek-total">{formatTRY(payableKurus)}</span>
            )}
          </span>
          {/* No chevron on an empty cart, there is nothing to expand. One
              chevron that turns, not two that swap: a swap has nothing to
              animate between, and the rotation is what says the sheet is
              moving rather than being replaced. */}
          {!cart.isEmpty && (
            <span className={`cart__peek-caret ${cartOpen ? 'is-open' : ''}`}>
              <ChevronUpIcon />
            </span>
          )}
        </button>
        {/* Leaving the ticket, from wherever the sheet happens to be. It used
            to disappear once the sheet opened and hand the job to a button in
            the header, but the strip is the thing that says which table is
            being edited, so the way out belongs beside it either way. */}
        {editing && (
          <button
            type="button"
            className="cart__peek-exit"
            onClick={() => closeTicket()}
            title="Adisyondan çık"
            aria-label="Adisyondan çık"
          >
            <XIcon />
          </button>
        )}
        </div>


        {/* The waiter added a round while the cashier had unsaved edits of
            their own. Overwriting silently would lose one or the other, so
            it says so and lets the cashier choose. */}
        {ticketChanged && (
          <div className="editing editing--changed">
            <span>Garson bu adisyona ekleme yaptı.</span>
            <button type="button" onClick={reloadTicket}>
              Yenile
            </button>
          </div>
        )}

        <div className="cart__head">
          <h2>
            {/* The table is named on the bar above; repeating it here said the
                same thing twice. Whose table it is answers the question that
                bar leaves open. */}
            {editing ? (
              editing.openedByName ? (
                <span>
                  {/* Masa adı en üstte. Bir zamanlar buradan kaldırılmıştı
                      çünkü üstteki çubuk da onu söylüyordu; salonlar geldikten
                      sonra ad "Bahçe 3" gibi bir şeye dönüştü ve sepetin
                      hangi masaya ait olduğu, kasada aynı anda açık duran
                      onlarca masanın arasında tek başına okunması gereken
                      bilgi oldu. */}
                  {editing.tableName && (
                    <span className="cart__head-table">
                      Masa: <b>{displayTable(editing.tableName)}</b>
                    </span>
                  )}
                  {/* Kiosk siparişi (30 Eki 2026): garson yok, sıra numarası var. */}
                  {editing.source === 'kiosk' ? (
                    <span className="cart__head-table">
                      Kiosk: <b>#{editing.takeawayNo ?? '-'}</b>
                    </span>
                  ) : (
                  <>
                  Garson:{' '}
                  {/* Everyone who has put something on this table, the opener
                      first. A second name appears the moment someone else adds
                      a round, each with the colour its lines carry below.

                      İki kişiden fazlaysa alt alta: yan yana akarken ikinci
                      isim ilkinin arkasına takılıyor ve renk noktaları hangi
                      isme ait olduğunu söyleyemiyordu. Tek kişide nokta zaten
                      çizilmiyor, o yüzden sarmalayıcı da gereksiz. */}
                  <span className={whoColors.size > 1 ? 'cart__head-names' : undefined}>
                  {[
                    editing.openedByName,
                    ...[...whoColors.keys()].filter((n) => n !== editing.openedByName),
                  ].map((who, i) => (
                    <span key={who}>
                      {/* No comma: each name already carries its own dot, and
                          the punctuation between two of them read as a third
                          mark rather than as a separator. Araya boşluk da
                          konmuyor, isimler artık alt alta ve aralarını
                          sütunun kendi gap'i açıyor. */}
                      {whoColors.size > 1 && (
                        <span
                          className="line__who"
                          style={{ background: whoColors.get(who) }}
                        />
                      )}
                      <span className="cart__head-who">{who}</span>
                    </span>
                  ))}
                  </span>
                  </>
                  )}
                </span>
              ) : (
                'Adisyon'
              )
            ) : (
              'Sipariş'
            )}
          </h2>
          {/* Sağ sütun: kaç ürün ve ne zaman açıldığı üstte, adisyonu boşaltan
              düğme altında. Sayım daha önce isimlerin altındaydı ve masaya iki
              kişi yazdığında isim listesi uzadıkça aşağı kayıyordu; buradan
              sabit duruyor. */}
          <div className="cart__head-right">
            {editing && (
              <span className="cart__head-meta">
                {editing.itemCount} ürün, {formatClock(editing.createdAtLocal)}
              </span>
            )}
            {/* Not on a waiter's screen. They work standing up with a phone in
                one hand, and a mis-tap here wipes someone's open tab, a
                correction that belongs at the counter, where the person making
                it is looking at the customer it affects. */}
            {/* Masa notu (4 Kas 2026): not yokken yalnız bu küçük düğme,
                varken başlığın altındaki kutu. Kiosk siparişinde masa yok. */}
            {((editing && editing.source !== 'kiosk' && !editing.tableNote) ||
              (!editing && pendingTable && !pendingNote)) && (
              <button
                type="button"
                className="cart__clear cart__note-add"
                onClick={() => setNoteFor(editing ?? 'pending')}
              >
                <NoteIcon />
                Not ekle
              </button>
            )}
            {canSettle && !cart.isEmpty && (
              <button
                type="button"
                className="cart__clear cart__clear--warn"
                onClick={() => askClear()}
              >
                Temizle
              </button>
            )}
          </div>
          {/* No exit here any more: it lives beside the strip above, which
              stays put whether the sheet is open or closed. Two of them on one
              screen only asked which was the right one. */}
        </div>

        {(editing?.tableNote || (!editing && pendingTable && pendingNote)) && (
          <button type="button" className="tnotebox" onClick={() => setNoteFor(editing ?? 'pending')} title="Notu düzenle">
            <span className="tnotebox__label">
              <NoteIcon />
              Masa notu
            </span>
            <span className="tnotebox__text">{editing ? editing.tableNote : pendingNote}</span>
            <span className="tnotebox__edit">
              <PencilIcon />
            </span>
          </button>
        )}

        <div className="lines">
          {cart.isEmpty ? (
            <div className="lines__empty">
              {/* Kiosk sekmesinde ödeme de burada (kullanıcı: "kioskun yeri ora olsun"). */}
              {kioskView ? 'Ödeme almak için soldan bir kiosk siparişine dokunun' : 'Ürün seçin'}
            </div>
          ) : (
            cartGroups.map((group) => {
              // A product ordered one way needs no heading and no breakdown:
              // "Su − 3 + 90,00 ₺" says everything. The split only appears once
              // a note distinguishes one portion from another.
              const plain = group.lines.length === 1 && !group.lines[0].note

              const wl = group.lines[0]
              if (wl.weigh) {
                const key = lineKey(wl)
                const pending = wl.weigh.status === 'pending'
                return (
                  <div key={key} className={`line line--solo wline ${pending ? 'wline--pending' : ''}`}>
                    <div className="wline__main">
                      <span className="wline__name">
                        {whoColors.size > 1 && wl.byName && (
                          <span className="line__who" style={{ background: whoColors.get(wl.byName) }} title={wl.byName} />
                        )}
                        <b>{wl.qty}×</b> {wl.name}
                      </span>
                      {pending ? (
                        <span className="wline__status">⚖ Tartım bekliyor</span>
                      ) : (
                        <span className="wline__info">
                          {formatGr(totalNetGr(wl.weigh.plates))}
                          {wl.weigh.tare ? `, ${wl.weigh.tare.name}` : ''}
                        </span>
                      )}
                    </div>
                    {pending && (
                      <span className="line__qty">
                        <button type="button" onClick={() => decrement(key, wl)} disabled={!canEditLine(wl)} aria-label="Azalt">−</button>
                        <span className="line__n">{wl.qty}</span>
                        <button type="button" onClick={() => cart.increment(key)} disabled={!canEditLine(wl)} aria-label="Artır">+</button>
                      </span>
                    )}
                    {canSettle ? (
                      <button
                        type="button"
                        className={`wline__tart ${pending ? 'is-pending' : ''}`}
                        onClick={() =>
                          setWeighing({
                            mode: 'line',
                            key,
                            line: wl,
                            product: flatProducts.find((x) => x.id === wl.productId),
                          })
                        }
                        title={pending ? 'Tart' : 'Yeniden tart'}
                      >
                        {pending ? '⚖ Tart' : formatTRY(wl.lineTotalKurus)}
                      </button>
                    ) : (
                      <span className="line__total">{pending ? '-' : formatTRY(wl.lineTotalKurus)}</span>
                    )}
                    {!pending && canSettle && (
                      <button type="button" className="wline__del" onClick={() => cart.remove(key)} aria-label="Satırı sil">
                        ×
                      </button>
                    )}
                  </div>
                )
              }

              if (plain) {
                const l = group.lines[0]
                const key = lineKey(l)
                return (
                  <div key={`${group.productId} ${group.byName ?? ''} ${group.portion ?? ''}`} className="line line--solo">
                    <button
                      type="button"
                      className="lgrp__name"
                      onClick={() =>
                        canEditLine(group)
                          ? onEditNote({
                              productId: group.productId,
                              name: group.name,
                              byName: group.byName,
                              portion: group.portion,
                            })
                          : say(`${group.byName} girdi, siz değiştiremezsiniz`, 'warn')
                      }
                      title="Not ekle (soğansız, acısız…)"
                    >
                      {/* Whose round this is, when more than one person has
                          added to the ticket. Hidden on a table with a single
                          contributor: a dot on every line that all means the
                          same thing is noise. */}
                      {whoColors.size > 1 && l.byName && (
                        <span
                          className="line__who"
                          style={{ background: whoColors.get(l.byName) }}
                          title={l.byName}
                        />
                      )}
                      {group.name}
                      {/* Marks what the ticket did not have when it was loaded.
                          On a table with three rounds on it, this is the only
                          way to see which one is yours before saving. */}
                      {editing && addedQty.has(key) && (
                        <span className="line__new">yeni</span>
                      )}
                    </button>
                    <span className="line__qty">
                      <button
                        type="button"
                        onClick={() => decrement(key, l)}
                        disabled={!canEditLine(l)}
                        aria-label="Azalt"
                      >
                        −
                      </button>
                      <span className="line__n">{l.qty}</span>
                      <button
                        type="button"
                        onClick={() => cart.increment(key)}
                        disabled={!canEditLine(l)}
                        aria-label="Artır"
                      >
                        +
                      </button>
                    </span>
                    {/* The amount is the control: an extra pencil beside it
                        would be a third button on an already narrow row. */}
                    {canEditPrice ? (
                      <button
                        type="button"
                        className="line__total line__total--edit"
                        onClick={() =>
                          canEditLine(l)
                            ? setPricing({ key, name: group.name, line: l })
                            : say(`${l.byName} girdi, siz değiştiremezsiniz`, 'warn')
                        }
                        title="Fiyatı değiştir (gramajlı satış)"
                      >
                        {formatTRY(l.lineTotalKurus)}
                        <PencilIcon />
                      </button>
                    ) : (
                      // Yetkisi yoksa kalem de yok: tıklanınca "olmaz" diyen bir
                      // düğme yerine düz tutar.
                      <span className="line__total">{formatTRY(l.lineTotalKurus)}</span>
                    )}
                  </div>
                )
              }

              return (
              <div key={`${group.productId} ${group.byName ?? ''} ${group.portion ?? ''}`} className="lgrp">
                {/* The heading is the note button for the whole product: every
                    portion of it is edited in one dialog. */}
                <button
                  type="button"
                  className="lgrp__name"
                  // Başlık da not penceresini açıyor (kullanıcı, 22 Eki 2026);
                  // eskiden basılamıyordu. Pencere bir kişinin porsiyonlarını
                  // yazdığı için basanın düzenleyebildiği ilk satırın kişisi
                  // alınıyor (tek garsonlu masada hepsi). 1. porsiyonda açılır.
                  onClick={() => {
                    const mine = group.lines.find((l) => !l.weigh && canEditLine(l))
                    if (!mine) {
                      const other = group.lines.find((l) => l.byName)
                      say(`${other?.byName ?? 'Başkası'} girdi, siz değiştiremezsiniz`, 'warn')
                      return
                    }
                    onEditNote({ productId: group.productId, name: group.name, byName: mine.byName, portion: group.portion })
                  }}
                  title="Not ekle (soğansız, acısız…)"
                >
                  {group.name}
                </button>

                {group.lines.map((l) => {
                  const key = lineKey(l)
                  return (
                    <div key={key} className="line">
                      <button
                        type="button"
                        className="line__variant"
                        onClick={() =>
                          canEditLine(l)
                            ? onEditNote({
                                productId: group.productId,
                                name: group.name,
                                byName: l.byName,
                                note: l.note ?? '',
                                portion: group.portion,
                              })
                            : say(`${l.byName} girdi, siz değiştiremezsiniz`, 'warn')
                        }
                        title="Not ekle (soğansız, acısız…)"
                      >
                        {/* "Normal" rather than blank: a row with nothing where
                            the others carry a note reads as missing data. */}
                        {whoColors.size > 1 && l.byName && (
                          <span
                            className="line__who"
                            style={{ background: whoColors.get(l.byName) }}
                            title={l.byName}
                          />
                        )}
                        {l.note || 'Normal'}
                        {/* Per portion, not per product: the same item can have
                            an old round and a new one under different notes. */}
                        {editing && addedQty.has(key) && (
                          <span className="line__new">yeni</span>
                        )}
                      </button>
                      <span className="line__qty">
                        <button
                          type="button"
                          onClick={() => decrement(key, l)}
                          disabled={!canEditLine(l)}
                          aria-label="Azalt"
                        >
                          −
                        </button>
                        <span className="line__n">{l.qty}</span>
                        <button
                          type="button"
                          onClick={() => cart.increment(key)}
                          disabled={!canEditLine(l)}
                          aria-label="Artır"
                        >
                          +
                        </button>
                      </span>
                      {canEditPrice ? (
                        <button
                          type="button"
                          className="line__total line__total--edit"
                          onClick={() =>
                          canEditLine(l)
                            ? setPricing({ key, name: group.name, line: l })
                            : say(`${l.byName} girdi, siz değiştiremezsiniz`, 'warn')
                        }
                          title="Fiyatı değiştir (gramajlı satış)"
                        >
                          {formatTRY(l.lineTotalKurus)}
                          <PencilIcon />
                        </button>
                      ) : (
                        // Yetkisi yoksa kalem de yok: tıklanınca "olmaz" diyen bir
                        // düğme yerine düz tutar.
                        <span className="line__total">{formatTRY(l.lineTotalKurus)}</span>
                      )}
                    </div>
                  )
                })}
              </div>
              )
            })
          )}
        </div>

        {hintVisible && (
          <div className="note-hint" role="note">
            <NotebookPenIcon />
            <span>
              <b>Ürünün adına dokunun</b>, not ekleyin: acısız, az pişmiş, paket servis…
            </span>
            <button type="button" onClick={hideNoteHint} aria-label="İpucunu kapat">
              ×
            </button>
          </div>
        )}

        {/* Kampanya: uyan kampanya varsa kasiyere öneri olarak çıkıyor,
            kendiliğinden uygulanmıyor. Yalnız ödeme alan ekranda. */}
        {canSettle &&
          (cart.tab === 'takeaway' || editing) &&
          !cart.isEmpty &&
          (appliedCampaigns.length > 0 || campaignOfferList.length > 0) && (
            <div className="camp" role="group" aria-label="Kampanyalar">
              {[...appliedCampaigns.map((o) => ({ o, on: true })), ...campaignOfferList.map((o) => ({ o, on: false }))].map(
                ({ o, on }) => (
                  <div key={o.campaign.id} className={on ? 'camp__offer camp--on' : 'camp__offer'}>
                    <div className="camp__row">
                      <GiftIcon />
                      <span className="camp__name">{o.campaign.name}</span>
                      <button
                        type="button"
                        className="camp__info"
                        aria-label={`${o.campaign.name} özeti`}
                        aria-expanded={campaignInfo === o.campaign.id}
                        onClick={() => setCampaignInfo(campaignInfo === o.campaign.id ? null : o.campaign.id)}
                      >
                        <InfoIcon />
                      </button>
                      <b className="camp__amt">−{formatTRY(o.kurus)}</b>
                      {on ? (
                        <button
                          type="button"
                          className="camp__x"
                          onClick={() => setCampaignIds((ids) => ids.filter((id) => id !== o.campaign.id))}
                          aria-label={`${o.campaign.name} kampanyasını kaldır`}
                          title="Kampanyayı kaldır"
                        >
                          <XIcon />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="camp__add"
                          onClick={() => setCampaignIds((ids) => [...ids, o.campaign.id])}
                        >
                          Ekle
                        </button>
                      )}
                    </div>
                    {campaignInfo === o.campaign.id && <p className="camp__sum">{campaignSummary(o.campaign)}</p>}
                  </div>
                ),
              )}
            </div>
          )}

        {/* Ara ödeme alınmış adisyon (22 Eki 2026): alınan ayrı satırda,
            büyük rakam kalan. */}
        {prepaidKurus > 0 && <PartialRows partials={partials} prepaidKurus={prepaidKurus} />}
        <div className="total">
          <span>{prepaidKurus > 0 ? 'KALAN' : 'TOPLAM'}</span>
          <span className="total__value">
            {(campaignKurus > 0 || prepaidKurus > 0 || loyaltyOff) && (
              <s className="total__was">{formatTRY(cart.totalKurus)}</s>
            )}
            {formatTRY(dueKurus)}
          </span>
        </div>
        {loyaltyOff && (
          <p className="total__loyalty">
            Sadakat ödülü: %{loyalty.rewardPercent} indirim (−{formatTRY(loyaltyOff.discountKurus)})
          </p>
        )}

        {/* A waiter never sees payment buttons, the ticket is handed to the
            till instead. That boundary is enforced in the rules too.
            The cashier sees them on Gel-Al, where money changes hands now, and
            on Masa once a ticket is loaded, settling a table's tab is the
            till's main job. A Masa basket with no ticket behind it is a tab
            being built, so that one ends the way a waiter's does: on a table. */}
        {canSettle && (cart.tab === 'takeaway' || editing) ? (
          <>
            {/* Any change to a loaded ticket, added or taken off. Both used to
                have their own row and only additions could be saved with
                paper; a correction had no way to reach the stall at all, even
                when the stall needed to know. One pair of buttons now: record
                it, or record it and choose what goes on the slip. */}
            {editing && hasEdit && (
              <div className="pay__add">
                <button
                  type="button"
                  className="pay__add-btn"
                  onClick={() => saveAddition(false)}
                >
                  <ReceiptOffIcon />
                  <i className="pay__add-rule" />
                  Değişiklikleri Kaydet
                </button>
                {usePrinter && (
                <button
                  type="button"
                  className="pay__add-btn pay__add-btn--print"
                  onClick={() => editing && setPrintPick(editing)}
                >
                  {/* Both jobs, in the order they happen: the ticket is saved
                      and then paper follows. The rule between them says they
                      are two things rather than one compound icon. */}
                  <ReceiptTextIcon />
                  <i className="pay__add-rule" />
                  Kaydet &amp; Yazdır
                </button>
                )}
              </div>
            )}

            {/* No cash/card split: the question at the counter is whether the
                money was taken, not what it was taken in. What differs between
                these three is only what happens to the paper. */}
            {weighPending && (
              <div className="weigh-lock" role="status">
                <span>⚖ Tartım bekleyen ürün var, ödeme için önce tartın</span>
                {canSettle && (
                  <button
                    type="button"
                    onClick={() => {
                      const l = cart.lines.find(isPendingWeigh)
                      if (l)
                        setWeighing({
                          mode: 'line',
                          key: lineKey(l),
                          line: l,
                          product: flatProducts.find((x) => x.id === l.productId),
                        })
                    }}
                  >
                    Tart
                  </button>
                )}
              </div>
            )}

            {/* Ödeme alanı (21 Eki 2026): Parçalı Öde'nin içindekiyle aynı
                parça. Yemek Kartı 4. düğme. */}
            <PayActions
              totalKurus={dueKurus}
              disabled={cart.isEmpty || weighPending}
              panel={dialog === 'tender' || dialog === 'bahsis' || dialog === 'meal' ? dialog : 'none'}
              onPanel={(p) => setDialog(p)}
              tenderPrint={tenderPrint}
              onTender={askTender}
              onDiscount={onOther}
              // Print is left to completeSale's own rule unless the tender said.
              onPay={(method, extras, print) => completeSale(method, extras, print)}
              bahsisLabel={hasFeature('sadaka') ? 'Bahşiş' : undefined}
              onDebtor={hasFeature('cari') ? () => setDialog('debtor') : undefined}
              // Only meaningful on a table already open: "someone else's share"
              // has no meaning for a walk-up sale that has not been saved.
              onSplit={editing ? () => setDialog('split') : undefined}
              splitDisabled={!editing || editing.lines.length === 0 || weighPending}
              showExtras={dialog === 'none'}
              mealOn={hasFeature('yemek')}
              onLoyalty={hasFeature('damga') ? () => setLoyaltyOpen(true) : undefined}
              loyaltyLabel={
                loyaltyPick
                  ? `${loyaltyPick.card.code}, ${loyaltyPick.useReward ? 'ödül' : `${Math.min(loyaltyPick.card.stamps, loyalty.target)}/${loyalty.target}`}`
                  : undefined
              }
            />
            {loyaltyOpen && (
              <LoyaltyDialog
                current={loyaltyPick}
                onAttach={(card, useReward) => setLoyaltyPick({ card, useReward })}
                onDetach={() => setLoyaltyPick(null)}
                onClose={() => setLoyaltyOpen(false)}
              />
            )}
          </>
        ) : editing ? (
          /* On a loaded ticket the two jobs are different and used to share one
             button that could only do the second: taking an item off the bill
             had no way to be saved at all. */
          <div className="pay2">
            <button
              type="button"
              className="pay2__btn"
              onClick={() => saveTicket({ name: editing.tableName ?? '' }, true)}
              disabled={!hasEdit}
              title={
                hasEdit
                  ? 'Adisyonu bu haliyle kaydet'
                  : 'Değişiklik yok'
              }
            >
              <ReceiptOffIcon />
              <i className="pay__add-rule" />
              Değişiklikleri Kaydet
            </button>
            <button
              type="button"
              className="pay2__btn pay2__btn--main"
              onClick={() => saveTicket({ name: editing.tableName ?? '' })}
              disabled={!hasAddition}
              title={hasAddition ? 'Yeni ürünleri masaya ekle' : 'Yeni ürün yok'}
            >
              <ReceiptTextIcon />
              <i className="pay__add-rule" />
              Masaya Ekle
            </button>
          </div>
        ) : (
          <div className="pay">
            <button
              type="button"
              className="pay__ticket"
              // A table already picked on the grid is the answer the dialog
              // would be asking for, so it saves straight there.
              onClick={() =>
                pendingTable ? saveTicket(pendingTable) : setDialog('table')
              }
              disabled={cart.isEmpty}
            >
              {/* Kenarda dönen ince çizgi (SalePage.css .pay__orbit). */}
              <svg className="pay__orbit" aria-hidden="true">
                {/* Kuyruk (25 Eki 2026): çizgi 100 ince parça, baştan sona
                    pürüzsüz saydamlaşıyor (8 parçada basamaklar görünüyordu). */}
                {ORBIT_TAIL.map(({ dash, opacity }, j) => (
                  <rect key={j} width="100%" height="100%" rx="7" pathLength={100} strokeDasharray={dash} opacity={opacity} />
                ))}
              </svg>
              {pendingTable
                ? `${displayTable(labelOf(pendingTable))} masasına kaydet`
                : 'Adisyona Kaydet'}
            </button>
          </div>
        )}
      </aside>

      {weighing && (
        <WeighDialog
          productName={weighing.mode === 'new' ? weighing.product.name : weighing.line.name}
          perKgKurus={
            weighing.mode === 'new'
              ? weighing.product.priceKurus
              : weighing.line.weigh?.perKgKurus ?? weighing.line.unitPriceKurus
          }
          defaultTare={
            weighing.mode === 'new'
              ? weighing.product.tare
              : weighing.line.weigh?.tare ?? weighing.product?.tare
          }
          tares={knownTares}
          initialCount={weighing.mode === 'new' ? 1 : weighing.line.qty}
          initialGross={weighing.mode === 'line' ? weighing.line.weigh?.plates?.map((p) => p.grossGr) : undefined}
          confirmVerb={weighing.mode === 'new' ? 'sepete ekle' : 'kaydet'}
          onCancel={() => setWeighing(null)}
          onConfirm={(tare, plates) => {
            if (weighing.mode === 'new') {
              const p = weighing.product
              cart.addWeighed(p, tare, plates, stationOf(p), admin?.displayName || admin?.username)
            } else {
              cart.applyWeigh(weighing.key, tare, plates, admin?.displayName || admin?.username)
            }
            setWeighing(null)
          }}
        />
      )}

      {portioning && (
        <PortionPicker
          product={portioning}
          onClose={() => setPortioning(null)}
          onPick={(portion) => {
            cart.add(portioning, stationOf(portioning), admin?.displayName || admin?.username, portion ?? undefined)
            setPortioning(null)
          }}
        />
      )}

      {noting && (
        <NoteDialog
          productName={noting.name}
          notes={notingSplit.fresh}
          sent={notingSplit.sent}
          startAt={notingStart}
          options={notingOptions}
          onCancel={() => setNoting(null)}
          onConfirm={(notes) => {
            // Gönderilmişler olduğu gibi, üstüne yeni porsiyonların notları.
            cart.setPortionNotes(noting.productId, [...notingSplit.sent, ...notes], noting.byName, noting.portion)
            setNoting(null)
          }}
        />
      )}

      {noteFor && (
        <TableNoteDialog
          tableLabel={
            (noteFor === 'pending'
              ? pendingTable && displayTable(labelOf(pendingTable))
              : displayTable(noteFor.tableName)) ?? 'Masa'
          }
          initial={noteFor === 'pending' ? pendingNote : (noteFor.tableNote ?? '')}
          onSave={(note) => {
            if (!tenantId) return
            // Henüz açılmamış masa: not kaydette adisyonla birlikte yazılır.
            if (noteFor === 'pending') return setPendingNote(note)
            if (isViewMode()) {
              window.dispatchEvent(new Event(VIEW_MODE_EVENT))
              return
            }
            setTicketNote(tenantId, noteFor.id, note)
            say(note ? 'Masa notu kaydedildi' : 'Masa notu silindi')
          }}
          onClose={() => setNoteFor(null)}
        />
      )}

      {movingOpen && (
        <MoveTicketDialog
          tables={myTables}
          openTickets={myTickets}
          onMove={onMoveTicket}
          onMerge={onMergeTickets}
          onCancel={() => setMovingOpen(false)}
        />
      )}

      {pricing && (
        <PriceDialog
          productName={pricing.name}
          unitPriceKurus={pricing.line.unitPriceKurus}
          menuPriceKurus={products.find((p) => p.id === pricing.line.productId)?.priceKurus}
          qty={pricing.line.qty}
          onCancel={() => setPricing(null)}
          onConfirm={(unitPriceKurus) => {
            cart.setUnitPrice(pricing.key, unitPriceKurus)
            setPricing(null)
          }}
        />
      )}

      {/* Keyed by the timestamp so a second save restarts the two seconds
          rather than inheriting what is left of the first one's. */}
      {scanning && <CameraScanner onCode={scanToCart} onClose={() => setScanning(false)} />}

      {closedTable && (
        <ClosedFlash key={closedTable.at} table={closedTable.name} onDone={() => setClosedTable(null)} />
      )}

      {toast && (
        <Toast
          key={toast.at}
          message={toast.text}
          tone={toast.tone}
          onDone={() => setToast(null)}
        />
      )}

      {/* Every question this screen asks, in the app's own voice rather than
          the browser's. */}
      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}

      {finding && (
        <FindTableDialog
          openTickets={myTickets}
          onCancel={() => setFinding(false)}
          onPick={(ticket) => {
            setFinding(false)
            pickTicket(ticket)
          }}
        />
      )}

      {/* Which lines go onto paper. The bill as it stands, so the cashier picks
          from what they can see rather than from a diff they cannot.

          Two ways in. From a correction in the cart, where the ticket being
          picked over is the one loaded and saving follows the printing. From
          the printer on a table card, where nothing is being changed, that
          one only reprints, so it must not touch the ticket. */}
      {printPick && (
        <PrintPickDialog
          tableName={displayTable(printPick.tableName) ?? 'Adisyon'}
          lines={editing?.id === printPick.id ? cart.lines : printPick.lines}
          addedQty={editing?.id === printPick.id ? addedQty : new Map()}
          onCancel={() => setPrintPick(null)}
          onConfirm={(lines) => {
            const forLoaded = editing?.id === printPick.id
            setPrintPick(null)
            if (lines.length === 0) return
            if (forLoaded) {
              saveAddition(true, lines)
              return
            }
            queuePrint([
              {
                ...printPick,
                lines,
                itemCount: lines.reduce((n, l) => n + l.qty, 0),
                totalKurus: lines.reduce((n, l) => n + l.lineTotalKurus, 0),
                // A fresh id: the ticket's own is already down as printed, and
                // reusing it would make the queue treat this as a duplicate.
                id: `${printPick.id}+${Date.now()}`,
                // Masa kartından yeniden basılan fiş "TEKRAR BASIM" yazsın
                // (25 Eki 2026): yeni sipariş sanılıp ikinci kez hazırlanmasın.
                reprint: true,
                createdAtLocal: Date.now(),
              },
            ])
          }}
        />
      )}

      {/* Garson önce masa seçiyor (25 Eki 2026). */}
      {dialog === 'tablePick' && (
        <TableDialog
          pickOnly
          tables={myTables}
          totalKurus={0}
          openTickets={openTickets}
          onCancel={() => setDialog('none')}
          onPick={(t) => {
            setDialog('none')
            onPickTable(t, openTickets.find((o) => o.tableName === labelOf(t)) ?? null)
          }}
          onAddToTicket={() => setDialog('none')}
        />
      )}

      {dialog === 'table' && (
        <TableDialog
          tables={myTables}
          totalKurus={cart.totalKurus}
          itemCount={cart.itemCount}
          openTickets={openTickets}
          onCancel={() => setDialog('none')}
          onPick={saveTicket}
          onAddToTicket={addCartToTicket}
        />
      )}

      {/* Kısmi and İndirimli decide printing the same way the three buttons
          above do: a walk-up sale prints, a waiter's ticket whose paper already
          went to the stalls does not. */}
      {dialog === 'other' && (
        <OtherPaymentSheet
          totalKurus={payableKurus}
          discounts={discounts}
          onSaveRates={can('settings') ? saveDiscountRates : undefined}
          onCancel={() => setDialog('none')}
          bahsisLabel={hasFeature('sadaka') ? 'Bahşiş' : undefined}
          onConfirm={(method, extras, print) => completeSale(method, extras, print)}
        />
      )}

      {dialog === 'split' && editing && (
        <SplitPaymentSheet
          ticket={editing}
          discounts={discounts}
          onSaveRates={can('settings') ? saveDiscountRates : undefined}
          onCancel={() => setDialog('none')}
          onSettlePart={settlePart}
          onSettleAmount={settleAmount}
          partials={partials}
          bahsisLabel={hasFeature('sadaka') ? 'Bahşiş' : undefined}
          cariOn={hasFeature('cari')}
          mealOn={hasFeature('yemek')}
        />
      )}

      {dialog === 'debtor' && (
        <DebtorPicker
          totalKurus={payableKurus}
          onCancel={() => setDialog('none')}
          onPick={(debtor, note) => void settleToDebtor(debtor, note)}
        />
      )}
    </div>
  )
}

/** Türkçe harfleri ve büyük/küçük farkını yok sayan karşılaştırma için. */
function foldTr(s: string): string {
  return s
    .toLocaleLowerCase('tr')
    .replace(/[çğıöşüâîû]/g, (c) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' })[c] ?? c)
}

/**
 * Ara ödemeler, tek tek: "1. ara ödeme · Kart −300 ₺". Listelenenler
 * adisyondaki toplamı tutmuyorsa (önceki günden kalan) fark ayrı satırda.
 */
export function PartialRows({ partials, prepaidKurus }: { partials: Order[]; prepaidKurus: number }) {
  const listed = partials.reduce((s, o) => s + o.totalKurus, 0)
  const rest = prepaidKurus - listed
  // İkiden fazlası tek satırda toplam (kullanıcı, 22 Eki 2026).
  if (partials.length > 2) {
    return (
      <div className="total-paid">
        <div className="total-paid__row">
          <span>Ara ödemeler ({partials.length})</span>
          <span>−{formatTRY(prepaidKurus)}</span>
        </div>
      </div>
    )
  }
  return (
    <div className="total-paid">
      {partials.map((o, i) => (
        <div key={o.id} className="total-paid__row">
          <span>
            {i + 1}. ara ödeme
            {o.paymentMethod && <small>, {PAYMENT_LABELS[o.paymentMethod]}</small>}
          </span>
          <span>−{formatTRY(o.totalKurus)}</span>
        </div>
      ))}
      {rest > 0 && (
        <div className="total-paid__row">
          <span>Önceki ara ödemeler</span>
          <span>−{formatTRY(rest)}</span>
        </div>
      )}
    </div>
  )
}
